import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import { invoicesAPI, customersAPI, billingAPI, billingOpsAPI, soCreateAPI, banksAPI, paymentsAPI, einvoiceAPI, salesCreditsAPI } from '@/lib/api';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';
import ItemSearchPicker from '@/components/ItemSearchPicker';
import { ChangeSerialDialog } from '@/components/ChangeSerialDialog';
import { CreditNoteLinesEditor } from '@/components/CreditNoteLinesEditor';
import { printInvoiceDocument, printSalesOrderDocument, invoiceTotals } from '@/lib/documentPrint';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  MagnifyingGlass, 
  Export, 
  Funnel, 
  X, 
  CaretDown, 
  CaretUp,
  ArrowLeft,
  User,
  CurrencyInr,
  CalendarBlank,
  Package,
  Receipt,
  ShoppingCart,
  Eye,
  ArrowRight,
  Money,
  DownloadSimple,
  Upload,
  Trash, PencilSimple, ShieldCheck, ClockCounterClockwise,
} from '@phosphor-icons/react';
import CreditNoteDetailDialog from '@/components/CreditNoteDetailDialog';
import PaymentDetailDialog from '@/components/PaymentDetailDialog';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';
import SearchableSelect from '@/components/SearchableSelect';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { SOCoreFieldsPanel, soCoreError } from '@/components/SOCoreFields';
import { INVOICE_TYPES } from '@/lib/mandatoryFields';

const csvList = (v) => String(v || '').split(',').map(x => x.trim()).filter(x => x && x !== 'all');
const INVOICE_STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid' }, { value: 'partial', label: 'Partially Paid' },
  { value: 'overdue', label: 'Overdue' }, { value: 'unpaid', label: 'Unpaid' },
  { value: 'draft', label: 'Draft' }, { value: 'void', label: 'Void' },
];
const SO_STATUS_OPTIONS = [
  { value: 'sent', label: 'Sent' }, { value: 'confirmed', label: 'Confirmed' },
  { value: 'invoiced', label: 'Invoiced' }, { value: 'partially_invoiced', label: 'Partially Invoiced' },
  { value: 'void', label: 'Void' }, { value: 'placed', label: 'Placed' }, { value: 'in_transit', label: 'In Transit' },
  { value: 'fulfilled', label: 'Fulfilled' }, { value: 'cancelled', label: 'Cancelled' },
];

const INV_PILL = {
  paid: { label: 'Paid', cls: 'paid' },
  partial: { label: 'Partially Paid', cls: 'partial' },
  overdue: { label: 'Overdue', cls: 'overdue' },
  unpaid: { label: 'Unpaid', cls: 'unpaid' },
  void: { label: 'Void', cls: 'void' },
  draft: { label: 'Draft', cls: 'draft' },
};
const invPill = (s) => INV_PILL[String(s || '').toLowerCase()] || { label: s || '—', cls: 'unpaid' };

// Trigger a browser download for a blob response (xlsx/csv exports)
const downloadBlobResponse = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};


export default function InvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [aggregates, setAggregates] = useState({});
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const bulkFileRef = useRef(null);
  const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const today = fmtLocalDate(new Date());

  // Read URL params for drill-down presets
  const urlStatus = searchParams.get('status');
  const urlClinicId = searchParams.get('clinic_id');
  const urlClinicName = searchParams.get('clinic_name');
  const urlCustomer = searchParams.get('customer');
  const urlStartDate = searchParams.get('start_date');
  const urlEndDate = searchParams.get('end_date');
  const urlTitle = searchParams.get('title');
  const urlSource = searchParams.get('source');

  const [filters, setFilters] = useState({
    status: urlStatus || 'all',
    search: urlCustomer || '',
    start_date: urlStartDate || today,
    end_date: urlEndDate || today,
    min_amount: '',
    max_amount: '',
    sort_by: 'invoice_date',
    sort_order: 'desc',
    clinic_id: urlClinicId || '',
    source: urlSource || 'all',
    lead_sources: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [invBranches, setInvBranches] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [selectedInvIds, setSelectedInvIds] = useState(new Set());
  const [drill, setDrill] = useState(null);
  const [exporting, setExporting] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    billingAPI.adminGetBranches?.().then(res => setInvBranches(res.data.branches || [])).catch(() => {});
    soCreateAPI.leadSources().then(res => setLeadSources(res.data.lead_sources || [])).catch(() => {});
  }, []);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: (page - 1) * pageSize,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.min_amount) params.min_amount = parseFloat(filters.min_amount);
      if (filters.max_amount) params.max_amount = parseFloat(filters.max_amount);
      if (filters.clinic_id) params.clinic_id = filters.clinic_id;
      if (filters.source && filters.source !== 'all') {
        if (filters.source === 'inter_branch') {
          params.inter_branch = 'only';
        } else if (filters.source === 'clinic_billing') {
          params.source = filters.source;
        }
      }
      if (filters.lead_sources?.length > 0) {
        params.sources = filters.lead_sources.join(',');
      }
      
      const response = await invoicesAPI.list(params);
      setInvoices(response.data.invoices || []);
      setTotal(response.data.total || 0);
      setAggregates(response.data.aggregates || {});
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleExportInvoices = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.min_amount) params.min_amount = parseFloat(filters.min_amount);
      if (filters.max_amount) params.max_amount = parseFloat(filters.max_amount);
      if (filters.clinic_id) params.clinic_id = filters.clinic_id;
      if (filters.source && filters.source !== 'all') {
        if (filters.source === 'inter_branch') params.inter_branch = 'only';
        else if (filters.source === 'clinic_billing') params.source = filters.source;
      }
      if (filters.lead_sources?.length > 0) params.sources = filters.lead_sources.join(',');
      const resp = await invoicesAPI.export(params);
      downloadBlobResponse(resp.data, `invoices_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Invoices exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  useEffect(() => {
    const onDel = () => { setSelectedInvoice(null); setInvoiceDetail(null); };
    window.addEventListener('invoice-deleted', onDel);
    return () => window.removeEventListener('invoice-deleted', onDel);
  }, []);

  const loadInvoiceDetail = async (invoiceId) => {
    setDetailLoading(true);
    setSelectedInvoice(invoiceId);
    try {
      const response = await invoicesAPI.get(invoiceId);
      setInvoiceDetail(response.data);
    } catch (error) {
      toast.error('Failed to load invoice details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedInvoice(null);
    setInvoiceDetail(null);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all', search: '', start_date: '', end_date: '',
      min_amount: '', max_amount: '', sort_by: 'invoice_date', sort_order: 'desc',
      clinic_id: '', source: 'all', lead_sources: [],
    });
    setSearchParams({});
    setPage(1);
  };

  const setToday = () => {
    setFilters(prev => ({ ...prev, start_date: today, end_date: today }));
    setPage(1);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await invoicesAPI.bulkUpload(file);
      toast.success(`${data.created} new invoices created${data.skipped ? `, ${data.skipped} existing updated` : ''}`);
      if (data.errors?.length) toast.error(`${data.errors.length} errors: ${data.errors[0]}`);
      loadInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDownloadTemplate = async () => {
    try {
      const { data } = await invoicesAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'invoice_bulk_upload_template.csv';
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const setThisMonth = () => {
    const now = new Date();
    const firstDay = fmtLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setFilters(prev => ({ ...prev, start_date: firstDay, end_date: today }));
    setPage(1);
  };

  const setAllTime = () => {
    setFilters(prev => ({ ...prev, start_date: '', end_date: '' }));
    setPage(1);
  };

  const isDrillDown = !!(urlStatus || urlClinicId || urlCustomer || urlTitle);
  const drillDownLabel = urlTitle || urlClinicName || (urlStatus ? `${urlStatus} invoices` : '') || (urlCustomer ? `Customer: ${urlCustomer}` : '');

  const hasActiveFilters = filters.min_amount || filters.max_amount || (filters.status !== 'all') || filters.clinic_id || (filters.source !== 'all') || (filters.lead_sources?.length > 0);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div data-testid="invoices-page">
      <PageHeader 
        title={isDrillDown ? `Drill Down: ${drillDownLabel}` : "Invoices & Sales Orders"}
        subtitle={`Manage all billing documents`}
        actions={
          <div className="flex gap-2">
            {isDrillDown && (
              <Button variant="outline" onClick={clearFilters} data-testid="exit-drilldown-btn">
                <ArrowLeft size={16} className="mr-2" />
                Exit Drill Down
              </Button>
            )}
            {activeTab === 'invoices' && (
              <>
                <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={handleExportInvoices} disabled={exporting} data-testid="export-invoices-btn">
                  <Export size={16} className="mr-2" />
                  {exporting ? 'Exporting…' : 'Export'}
                </Button>
                <input type="file" accept=".csv,.xlsx" ref={bulkFileRef} onChange={handleBulkUpload} className="hidden" />
                <Button variant="outline" onClick={() => bulkFileRef.current?.click()} disabled={uploading} data-testid="bulk-upload-inv-btn">
                  <Upload size={16} className="mr-1.5" />
                  {uploading ? 'Uploading...' : 'Bulk Upload'}
                </Button>
                <Button variant="outline" onClick={handleDownloadTemplate} data-testid="sample-file-inv-btn">
                  <DownloadSimple size={16} className="mr-1.5" />
                  Sample File
                </Button>
              </>
            )}
          </div>
        }
      />
      
      <div className="page-content">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1" data-testid="invoices-page-tabs">
            <TabsTrigger value="invoices" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">
              <Receipt size={16} className="mr-1.5" /> Invoices
            </TabsTrigger>
            <TabsTrigger value="sales-orders" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">
              <ShoppingCart size={16} className="mr-1.5" /> Sales Orders
            </TabsTrigger>
            <TabsTrigger value="credit-notes" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">
              <CurrencyInr size={16} className="mr-1.5" /> Credit Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="invoices">
        {/* Aggregates Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <p className="text-xs text-[#64748B] mb-1">Filtered Total Amount</p>
            <p className="text-lg font-semibold font-['Plus_Jakarta_Sans'] text-[#0F172A]" data-testid="filtered-total">
              {formatCurrency(aggregates.total_amount || 0)}
            </p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <p className="text-xs text-[#64748B] mb-1">GST Amount</p>
            <p className="text-lg font-semibold font-['Plus_Jakarta_Sans'] text-[#0F172A]">
              {formatCurrency(aggregates.total_gst || 0)}
            </p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <p className="text-xs text-[#64748B] mb-1">Outstanding Balance</p>
            <p className="text-lg font-semibold font-['Plus_Jakarta_Sans'] text-[#DC2626]">
              {formatCurrency(aggregates.total_balance || 0)}
            </p>
          </div>
        </div>

        {/* Search + Filter Bar */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-4" data-testid="invoices-filter-bar">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                placeholder="Search by invoice number or customer..."
                value={filters.search}
                onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1); }}
                className="pl-10"
                data-testid="search-invoices"
              />
            </div>
            <MultiSelectFilter
              value={csvList(filters.status)}
              onChange={(vals) => { setFilters(prev => ({ ...prev, status: vals.length ? vals.join(',') : 'all' })); setPage(1); }}
              options={INVOICE_STATUS_OPTIONS} allLabel="All Status" noun="statuses" testid="filter-status" buttonClassName="w-40"
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-60 justify-between" data-testid="filter-source">
                  <span className="truncate">
                    {filters.lead_sources?.length > 0
                      ? `${filters.lead_sources.length} cohort${filters.lead_sources.length > 1 ? 's' : ''} selected`
                      : filters.source === 'clinic_billing' ? 'Clinic Billing'
                      : filters.source === 'inter_branch' ? 'Inter-Branch Only'
                      : 'All Lead Sources'}
                  </span>
                  <CaretDown size={14} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2 max-h-96 overflow-y-auto" align="start">
                <div className="flex items-center justify-between mb-2 pb-2 border-b">
                  <button className="text-xs text-[#0F4C81] hover:underline" onClick={() => { setFilters(prev => ({ ...prev, source: 'all', lead_sources: [] })); setPage(1); }} data-testid="ls-clear-all">Clear all</button>
                  <button className="text-xs text-[#0F4C81] hover:underline" onClick={() => { setFilters(prev => ({ ...prev, source: 'all', lead_sources: leadSources.map(l => l.name) })); setPage(1); }} data-testid="ls-select-all">Select all ({leadSources.length})</button>
                </div>
                {/* Special filters first (single-select-like, mutually exclusive with cohorts) */}
                <label className="flex items-center gap-2 py-1.5 px-1 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="radio" name="src-special" checked={filters.source === 'clinic_billing'} onChange={() => { setFilters(prev => ({ ...prev, source: 'clinic_billing', lead_sources: [] })); setPage(1); }} data-testid="ls-clinic-billing" />
                  <span className="text-sm font-medium">Clinic Billing only</span>
                </label>
                <label className="flex items-center gap-2 py-1.5 px-1 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="radio" name="src-special" checked={filters.source === 'inter_branch'} onChange={() => { setFilters(prev => ({ ...prev, source: 'inter_branch', lead_sources: [] })); setPage(1); }} data-testid="ls-inter-branch" />
                  <span className="text-sm font-medium">Inter-Branch only</span>
                </label>
                <div className="h-px bg-[#E2E8F0] my-2" />
                <p className="text-[10px] uppercase tracking-wide text-[#64748B] px-1 mb-1">Cohorts (multi-select)</p>
                {leadSources.map(ls => {
                  const checked = filters.lead_sources?.includes(ls.name);
                  return (
                    <label key={ls.id} className="flex items-center gap-2 py-1.5 px-1 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!checked}
                        onChange={() => {
                          setFilters(prev => {
                            const cur = new Set(prev.lead_sources || []);
                            if (cur.has(ls.name)) cur.delete(ls.name); else cur.add(ls.name);
                            return { ...prev, source: 'all', lead_sources: Array.from(cur) };
                          });
                          setPage(1);
                        }}
                        data-testid={`ls-check-${ls.name}`}
                      />
                      <span className="text-sm flex-1 truncate">{ls.name}</span>
                      {ls.count ? <span className="text-[10px] text-[#64748B]">{ls.count.toLocaleString('en-IN')}</span> : null}
                    </label>
                  );
                })}
              </PopoverContent>
            </Popover>
            <MultiSelectFilter
              value={csvList(filters.clinic_id)}
              onChange={(vals) => { setFilters(prev => ({ ...prev, clinic_id: vals.join(',') })); setPage(1); }}
              options={invBranches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))}
              allLabel="All Locations" noun="locations" testid="filter-location" buttonClassName="w-52"
            />
            {selectedInvIds.size > 0 && (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-invoices-btn"
                onClick={async () => {
                  if (!window.confirm(`Delete ${selectedInvIds.size} invoice(s)? Linked serials will be released. This cannot be undone.`)) return;
                  try {
                    const { data } = await billingAPI.adminBulkDeleteInvoices(Array.from(selectedInvIds));
                    toast.success(`Deleted ${data.deleted} invoice(s), released ${data.serials_released || 0} serial(s)`);
                    setSelectedInvIds(new Set());
                    loadInvoices();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
                }}
              >
                <Trash size={14} className="mr-1" /> Delete Selected ({selectedInvIds.size})
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">From</label>
                <Input type="date" value={filters.start_date} onChange={(e) => { setFilters(prev => ({ ...prev, start_date: e.target.value })); setPage(1); }} className="w-40 h-9" data-testid="filter-start-date" />
              </div>
              <span className="text-[#64748B] mt-4">—</span>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">To</label>
                <Input type="date" value={filters.end_date} onChange={(e) => { setFilters(prev => ({ ...prev, end_date: e.target.value })); setPage(1); }} className="w-40 h-9" data-testid="filter-end-date" />
              </div>
            </div>
            <div className="h-6 w-px bg-[#E2E8F0]" />
            <div className="flex bg-[#F8FAFC] rounded-lg p-0.5">
              <button onClick={setToday} className={`px-2.5 py-1 text-xs font-medium rounded-md ${filters.start_date === today && filters.end_date === today ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="quick-today">Today</button>
              <button onClick={setThisMonth} className={`px-2.5 py-1 text-xs font-medium rounded-md ${filters.start_date && filters.start_date !== today && filters.end_date === today ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="quick-month">This Month</button>
              <button onClick={setAllTime} className={`px-2.5 py-1 text-xs font-medium rounded-md ${!filters.start_date && !filters.end_date ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="quick-all">All Time</button>
            </div>
            <Button 
              variant={showFilters ? "default" : "outline"} 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? "bg-[#0F4C81]" : ""}
              size="sm"
              data-testid="toggle-filters"
            >
              <Funnel size={14} className="mr-1" />
              Amount
              {(filters.min_amount || filters.max_amount) && <span className="ml-1 bg-[#DC2626] text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                <X size={14} className="mr-1" /> Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Amount Filters (toggle) */}
        {showFilters && (
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 mb-4 grid grid-cols-2 gap-4 max-w-md" data-testid="expanded-filters">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Min Amount</label>
              <Input type="number" placeholder="0" value={filters.min_amount} onChange={(e) => { setFilters(prev => ({ ...prev, min_amount: e.target.value })); setPage(1); }} data-testid="filter-min-amount" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Max Amount</label>
              <Input type="number" placeholder="No limit" value={filters.max_amount} onChange={(e) => { setFilters(prev => ({ ...prev, max_amount: e.target.value })); setPage(1); }} data-testid="filter-max-amount" />
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-8">
                  <input type="checkbox" checked={selectedInvIds.size === invoices.length && invoices.length > 0}
                    onChange={() => {
                      if (selectedInvIds.size === invoices.length && invoices.length > 0) setSelectedInvIds(new Set());
                      else setSelectedInvIds(new Set(invoices.map(i => i.id).filter(Boolean)));
                    }}
                    className="cursor-pointer" data-testid="invoice-select-all" />
                </th>
                <SortableHeader label="Invoice #" field="invoice_number" filters={filters} setFilters={setFilters} setPage={setPage} />
                <th>Customer</th>
                <th>Items</th>
                <SortableHeader label="Date" field="invoice_date" filters={filters} setFilters={setFilters} setPage={setPage} />
                <SortableHeader label="Amount" field="total" filters={filters} setFilters={setFilters} setPage={setPage} />
                <th>Balance</th>
                <th>Location</th>
                <th>Lead Source</th>
                <th>Source</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(11)].map((_, j) => (
                      <td key={j}><div className="skeleton h-4 w-20 rounded" /></td>
                    ))}
                  </tr>
                ))
              ) : invoices.length > 0 ? (
                invoices.map((invoice) => {
                  const itemsList = invoice.items || (invoice.line_items ? invoice.line_items.map(li => li.name || li.product || '') : []);
                  const invId = invoice.id || invoice.zoho_id;
                  return (
                  <tr 
                    key={invId} 
                    data-testid={`invoice-row-${invoice.invoice_number}`}
                    onClick={() => loadInvoiceDetail(invId)}
                    className="cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                  >
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedInvIds.has(invId)}
                        onChange={() => setSelectedInvIds(prev => { const n = new Set(prev); if (n.has(invId)) n.delete(invId); else n.add(invId); return n; })}
                        className="cursor-pointer" data-testid={`invoice-select-${invId}`} />
                    </td>
                    <td className="font-medium text-[#0F4C81]">{invoice.invoice_number}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div>
                        {invoice.customer_name ? (
                          <button onClick={() => setDrill({ id: invoice.customer_id, name: invoice.customer_name, phone: invoice.customer_phone })} className="text-sm text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`invoice-customer-${invoice.invoice_number}`}>{invoice.customer_name}</button>
                        ) : <p className="text-sm">-</p>}
                      </div>
                    </td>
                    <td className="max-w-[220px]">
                      {itemsList.length > 0 ? (
                        <div className="space-y-0.5">
                          {itemsList.map((item, idx) => (
                            <div key={idx} className="text-xs truncate" title={item}>{item}</div>
                          ))}
                        </div>
                      ) : <span className="text-xs text-[#64748B]">-</span>}
                    </td>
                    <td className="text-[#64748B]">{formatDate(invoice.invoice_date)}</td>
                    <td className="font-medium">{formatCurrency(invoice.total)}</td>
                    <td className={invoice.balance > 0 ? 'text-[#DC2626] font-medium' : 'text-[#64748B]'}>
                      {formatCurrency(invoice.balance || 0)}
                    </td>
                    <td className="text-xs text-[#64748B] max-w-[160px]" title={invoice.clinic_name || invoice.location_name}>
                      <div className="truncate">{invoice.clinic_name || invoice.location_name || '-'}</div>
                    </td>
                    <td className="text-xs text-[#64748B]">{invoice.lead_source || '-'}</td>
                    <td className="text-xs text-[#64748B]">{invoice.source || '-'}</td>
                    <td>
                      <span className={`status-pill ${invPill(invoice.status).cls}`}>{invPill(invoice.status).label}</span>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-[#64748B]">No invoices found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-[#64748B]">
              Showing {((page - 1) * pageSize + 1).toLocaleString()} to {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={page === 1} onClick={() => setPage(1)} size="sm">First</Button>
              <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} size="sm">Prev</Button>
              <span className="text-sm px-2">Page {page} of {totalPages}</span>
              <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} size="sm">Next</Button>
              <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(totalPages)} size="sm">Last</Button>
            </div>
          </div>
        )}
      

            {/* Invoice Detail Dialog */}
            <Dialog open={!!selectedInvoice} onOpenChange={closeDetail}>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                  <DialogTitle className="font-['Plus_Jakarta_Sans']">Invoice Details</DialogTitle>
                </DialogHeader>
                {detailLoading ? (
                  <div className="py-12 text-center">
                    <div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : invoiceDetail ? (
                  <InvoiceDetailView data={invoiceDetail} onPaymentRecorded={() => {
                    if (selectedInvoice) {
                      setDetailLoading(true);
                      invoicesAPI.get(selectedInvoice).then(r => setInvoiceDetail(r.data)).finally(() => setDetailLoading(false));
                      loadInvoices();
                    }
                  }} />
                ) : null}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="sales-orders">
            <AdminSalesOrdersTab />
          </TabsContent>

          <TabsContent value="credit-notes">
            <AdminCreditNotesTab />
          </TabsContent>
        </Tabs>
      </div>
      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} customerPhone={drill?.phone || ''} />
    </div>
  );
}

function SortableHeader({ label, field, filters, setFilters, setPage }) {
  const isActive = filters.sort_by === field;
  const toggle = () => {
    setFilters(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'desc' ? 'asc' : 'desc',
    }));
    setPage(1);
  };
  return (
    <th onClick={toggle} className="cursor-pointer select-none hover:text-[#0F4C81]">
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (filters.sort_order === 'desc' ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" />)}
      </span>
    </th>
  );
}

function InvoiceDetailView({ data, onPaymentRecorded }) {
  const { invoice, customer, payments, journal_entries, credit_notes } = data;
  const [showPayForm, setShowPayForm] = useState(false);
  const [payData, setPayData] = useState({ amount: '', payment_date: new Date().toISOString().split('T')[0], payment_mode: 'bank_transfer', reference: '', deposit_to: '', notes: '' });
  const [payingSaving, setPayingSaving] = useState(false);
  const [payBanks, setPayBanks] = useState([]);
  const [payModes, setPayModes] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [applyCredit, setApplyCredit] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedCN, setSelectedCN] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCNForm, setShowCNForm] = useState(false);
  const [cnData, setCnData] = useState({ cn_date: new Date().toISOString().split('T')[0], reason: '', notes: '' });
  const [cnSaving, setCnSaving] = useState(false);
  const initCnLines = () => (invoice.line_items || []).map(li => ({
    ...li, _include: true, quantity: Number(li.quantity || 1), rate: Number(li.rate || li.price || 0),
    discount_amount: Number(li.discount_amount ?? li.discount ?? 0), tax_percent: Number(li.tax_percent || 0),
    serial_numbers: li.serial_numbers || [],
    _origQty: Number(li.quantity || 1), _origDisc: Number(li.discount_amount ?? li.discount ?? 0), _origSerials: li.serial_numbers || [],
  }));
  const [cnLines, setCnLines] = useState(initCnLines);
  const setCnLine = (i, patch) => setCnLines(prev => prev.map((l, j) => {
    if (j !== i) return l;
    if ('quantity' in patch) {
      const q = Math.max(Number(patch.quantity) || 0, 0);
      const ratio = l._origQty ? Math.min(q / l._origQty, 1) : 1;
      return { ...l, ...patch, discount_amount: Math.round(l._origDisc * ratio * 100) / 100, serial_numbers: ((l.serial_numbers || []).length >= Math.ceil(q) ? l.serial_numbers : l._origSerials).slice(0, Math.ceil(q)) };
    }
    return { ...l, ...patch };
  }));
  const openCNForm = () => { setCnLines(initCnLines()); setShowCNForm(true); };
  const [einv, setEinv] = useState(invoice.einvoice || null);
  const [pushingIrp, setPushingIrp] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [serialTarget, setSerialTarget] = useState(null);

  useEffect(() => {
    const iid = invoice.id || invoice.invoice_id;
    if (!iid) return;
    invoicesAPI.audit(iid).then(({ data }) => setAuditLog(data.audit || [])).catch(() => {});
  }, [invoice.id, invoice.invoice_id]);

  const handlePushIrp = async () => {
    setPushingIrp(true);
    try {
      const { data } = await einvoiceAPI.pushToIrp(invoice.id || invoice.invoice_id);
      setEinv(data.einvoice);
      toast.success('Invoice pushed to IRP — IRN generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to push to IRP'); }
    finally { setPushingIrp(false); }
  };

  const alreadyCredited = !!(invoice.credit_note_id || invoice.payment_status === 'credit_noted' || (credit_notes && credit_notes.length > 0));

  const handleCreateCreditNote = async () => {
    setCnSaving(true);
    try {
      const { data: cn } = await billingAPI.adminCreateCreditNote({
        invoice_id: invoice.id || invoice.invoice_id,
        cn_date: cnData.cn_date,
        reason: cnData.reason,
        notes: cnData.notes,
        line_items: cnLines.filter(l => l._include).map(({ _include, _origQty, _origDisc, _origSerials, ...l }) => l),
      });
      toast.success(`Credit Note ${cn.cn_number} created`);
      setShowCNForm(false);
      if (onPaymentRecorded) onPaymentRecorded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create credit note');
    } finally { setCnSaving(false); }
  };

  const handleDeleteInvoice = async () => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This will release any linked serial numbers back to inventory. This action cannot be undone.`)) return;
    setDeleting(true);
    try {
      await invoicesAPI.delete(invoice.id || invoice.invoice_id);
      toast.success(`Invoice ${invoice.invoice_number} deleted`);
      if (onPaymentRecorded) onPaymentRecorded(); // refresh parent list
      // close dialog — parent handles via onOpenChange; dispatch a custom close:
      window.dispatchEvent(new CustomEvent('invoice-deleted', { detail: invoice.id }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete invoice');
    } finally { setDeleting(false); }
  };

  const handleRecordPayment = async () => {
    const amt = parseFloat(payData.amount) || 0;
    const credit = parseFloat(applyCredit) || 0;
    if (amt <= 0 && credit <= 0) { toast.error('Enter an amount or apply credit'); return; }
    if (amt > 0 && !(payData.notes || '').trim()) { toast.error('Remark is required'); return; }
    const balance = Math.max(Number(invoice.balance ?? invoice.balance_due ?? invoice.total ?? invoice.grand_total ?? 0) - credit, 0);
    const excess = amt > balance ? Math.round((amt - balance) * 100) / 100 : 0;
    const payPortion = Math.round((amt - excess) * 100) / 100;
    setPayingSaving(true);
    try {
      if (payPortion > 0 || credit > 0) {
        await billingOpsAPI.recordPayment(invoice.id || invoice.invoice_id, {
          amount: payPortion,
          payment_date: payData.payment_date,
          payment_mode: payData.payment_mode,
          reference_number: payData.reference,
          deposit_to: payData.deposit_to,
          notes: payData.notes,
          apply_credit_amount: credit,
        });
      }
      if (excess > 0) {
        await billingOpsAPI.recordAdvance({
          customer_id: invoice.customer_id || '',
          customer_name: invoice.customer_name || invoice.customer?.name || '',
          amount: excess,
          payment_mode: payData.payment_mode,
          deposit_to: payData.deposit_to,
          payment_date: payData.payment_date,
          reference: payData.reference,
          notes: `Excess from ${invoice.invoice_number} saved as advance`,
          clinic_id: invoice.clinic_id || '',
          clinic_name: invoice.clinic_name || '',
        });
        toast.success(`Payment recorded — ₹${excess.toLocaleString('en-IN')} saved as customer advance`);
      } else {
        toast.success('Payment recorded');
      }
      setShowPayForm(false);
      setPayData({ amount: '', payment_date: new Date().toISOString().split('T')[0], payment_mode: 'bank_transfer', reference: '', deposit_to: '', notes: '' });
      setApplyCredit('');
      if (onPaymentRecorded) onPaymentRecorded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setPayingSaving(false); }
  };

  useEffect(() => {
    if (!showPayForm) return;
    banksAPI.list().then(({ data }) => setPayBanks(data.banks || [])).catch(() => {});
    billingOpsAPI.listPaymentModes().then(({ data }) => setPayModes(data.payment_modes || [])).catch(() => {});
    const cid = invoice.customer_id;
    if (cid) paymentsAPI.customerCredit(cid).then(({ data }) => setCreditBalance(data.credit_balance || 0)).catch(() => setCreditBalance(0));
  }, [showPayForm, invoice.customer_id]);

  const handleDownloadInvoice = () => {
    printInvoiceDocument(invoice, { customer, payments });
  };
  
  return (
    <div className="space-y-6" data-testid="invoice-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-xl font-bold font-['Plus_Jakarta_Sans'] text-[#0F172A] whitespace-nowrap">{invoice.invoice_number}</h2>
          <p className="text-sm text-[#64748B]">Date: {formatDate(invoice.invoice_date)} | Due: {formatDate(invoice.due_date)}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadInvoice} data-testid="download-invoice-btn">
            <DownloadSimple size={14} className="mr-1" /> Download
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowEditForm(true)} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="edit-invoice-btn">
            <PencilSimple size={14} className="mr-1" /> Edit
          </Button>
          {auditLog.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowAudit(s => !s)} className="text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="invoice-history-btn">
              <ClockCounterClockwise size={14} className="mr-1" /> History ({auditLog.length})
            </Button>
          )}
          {!alreadyCredited && (
            <Button variant="outline" size="sm" onClick={openCNForm} className="text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="create-credit-note-btn">
              <CurrencyInr size={14} className="mr-1" /> Create Credit Note
            </Button>
          )}
          {alreadyCredited && invoice.credit_note_number && (
            <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200" data-testid="credited-badge">CN: {invoice.credit_note_number}</span>
          )}
          {(!einv || einv.status !== 'pushed') && (
            <Button variant="outline" size="sm" onClick={handlePushIrp} disabled={pushingIrp} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-testid="push-irp-btn">
              <ShieldCheck size={14} className="mr-1" /> {pushingIrp ? 'Pushing...' : 'Push to IRP'}
            </Button>
          )}
          {einv && einv.status === 'pushed' && (
            <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="einvoice-pushed-badge">e-Invoice ✓ IRN Generated</span>
          )}
          {(invoice.balance > 0 || invoice.status !== 'paid' || invoice.payment_status !== 'paid') && (
            <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={() => setShowPayForm(!showPayForm)} data-testid="record-payment-btn">
              <Money size={14} className="mr-1" /> Record Payment
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDeleteInvoice} disabled={deleting} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-invoice-btn">
            <Trash size={14} className="mr-1" /> {deleting ? 'Deleting...' : 'Delete'}
          </Button>
          <span className={`status-pill ${invPill(invoice.status).cls} text-sm`}>{invPill(invoice.status).label}</span>
        </div>
      </div>

      {/* Edit history / audit trail */}
      {showAudit && auditLog.length > 0 && (
        <div className="border border-slate-200 bg-slate-50/50 rounded-lg p-4" data-testid="invoice-audit-panel">
          <p className="font-semibold text-slate-800 flex items-center gap-1.5 mb-3 text-sm"><ClockCounterClockwise size={16} /> Edit History</p>
          <div className="space-y-3">
            {auditLog.map((log) => (
              <div key={log.id} className="text-xs border-l-2 border-slate-300 pl-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-700">{log.edited_by}{log.edited_by_role ? ` · ${log.edited_by_role}` : ''}</span>
                  <span className="text-slate-400">{formatDate(log.timestamp)} {(log.timestamp || '').slice(11, 16)}</span>
                </div>
                <div className="mt-1 space-y-0.5">
                  {(log.changes || []).map((ch, i) => (
                    <div key={i} className="text-slate-600">
                      <span className="font-medium">{ch.field}:</span>{' '}
                      <span className="text-red-600 line-through">{String(ch.old ?? '—')}</span>{' → '}
                      <span className="text-green-700">{String(ch.new ?? '—')}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* E-Invoice IRN / QR panel */}
      {einv && einv.status === 'pushed' && (
        <div className="border border-indigo-200 bg-indigo-50/40 rounded-lg p-4" data-testid="einvoice-panel">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 text-sm">
              <p className="font-semibold text-indigo-800 flex items-center gap-1"><ShieldCheck size={16} weight="fill" /> e-Invoice Generated (IRP)</p>
              <p className="text-xs text-[#64748B]">IRN</p>
              <p className="font-mono text-[11px] break-all text-[#0F172A] max-w-md">{einv.irn}</p>
              <div className="flex gap-6 pt-1">
                <div><p className="text-xs text-[#64748B]">Ack No.</p><p className="font-mono text-xs">{einv.ack_no}</p></div>
                <div><p className="text-xs text-[#64748B]">Ack Date</p><p className="text-xs">{einv.ack_date}</p></div>
              </div>
              {einv.mocked && <p className="text-[10px] text-amber-600 pt-1">Sandbox/MOCK IRN — connect a live GSP to generate real IRNs.</p>}
            </div>
            {einv.qr_base64 && <img src={einv.qr_base64} alt="e-Invoice QR" className="w-28 h-28 border border-indigo-200 rounded bg-white p-1" data-testid="einvoice-qr" />}
          </div>
        </div>
      )}

      {/* Record Payment Form */}
      {showPayForm && (
        <div className="border border-[#0F4C81] bg-[#EBF3FA] rounded-lg p-4 space-y-3" data-testid="payment-form">
          <h4 className="text-sm font-semibold text-[#0F4C81]">Record Payment</h4>
          {creditBalance > 0 && (
            <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-md px-3 py-2" data-testid="credit-available-banner">
              <span className="text-sm text-purple-800">Credits Available: <strong>₹{Number(creditBalance).toLocaleString('en-IN', {maximumFractionDigits:2})}</strong></span>
              <Button size="sm" variant="outline" className="h-7 text-purple-700 border-purple-300 hover:bg-purple-100" data-testid="apply-credit-btn"
                onClick={() => setApplyCredit(String(Math.min(creditBalance, invoice.balance_due ?? invoice.balance ?? invoice.grand_total ?? creditBalance)))}>
                Apply Now
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Amount</label>
              <Input type="number" value={payData.amount} onChange={e => setPayData(p => ({ ...p, amount: e.target.value }))} placeholder={String(invoice.balance_due ?? invoice.balance ?? invoice.total ?? 0)} data-testid="payment-amount" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Date</label>
              <Input type="date" value={payData.payment_date} onChange={e => setPayData(p => ({ ...p, payment_date: e.target.value }))} data-testid="payment-date" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Mode</label>
              <select value={payData.payment_mode} onChange={e => setPayData(p => ({ ...p, payment_mode: e.target.value }))} className="w-full h-9 border border-[#E2E8F0] rounded-md px-2 text-sm" data-testid="payment-mode">
                {payModes.length > 0 ? (
                  payModes.map(m => <option key={m.code} value={m.code}>{m.label}</option>)
                ) : (
                  <>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Deposit To</label>
              <select value={payData.deposit_to} onChange={e => setPayData(p => ({ ...p, deposit_to: e.target.value }))} className="w-full h-9 border border-[#E2E8F0] rounded-md px-2 text-sm" data-testid="payment-deposit-to">
                <option value="">Select account</option>
                {payBanks.map(b => <option key={b.id} value={b.account_name}>{b.account_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Reference</label>
              <Input value={payData.reference} onChange={e => setPayData(p => ({ ...p, reference: e.target.value }))} placeholder="Ref #" data-testid="payment-ref" />
            </div>
            <div className="col-span-2 md:col-span-4">
              <label className="text-xs text-[#64748B] block mb-1">Remark *</label>
              <Input value={payData.notes} onChange={e => setPayData(p => ({ ...p, notes: e.target.value }))} placeholder="Payment remark (required)" data-testid="payment-remark" />
            </div>
            {creditBalance > 0 && (
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Apply Credit</label>
                <Input type="number" value={applyCredit} onChange={e => setApplyCredit(e.target.value)} placeholder="0" max={creditBalance} data-testid="payment-apply-credit" />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleRecordPayment} disabled={payingSaving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="payment-submit">
              {payingSaving ? 'Saving...' : 'Save Payment'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowPayForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Amounts */}
      {(() => {
        const t = invoiceTotals(invoice);
        const cells = [
          ['Sub Total', formatCurrency(t.gross), 'inv-amt-subtotal'],
          ['Discount', t.discount ? `- ${formatCurrency(t.discount)}` : formatCurrency(0), 'inv-amt-discount'],
          ...(t.igst ? [['IGST', formatCurrency(t.igst), 'inv-amt-igst']] : [['CGST', formatCurrency(t.cgst), 'inv-amt-cgst'], ['SGST', formatCurrency(t.sgst), 'inv-amt-sgst']]),
          ['Total', formatCurrency(invoice.total), 'inv-amt-total'],
        ];
        return (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {cells.map(([label, val, tid]) => (
              <div key={label} className="bg-[#F8FAFC] rounded-lg p-3" data-testid={tid}>
                <p className="text-xs text-[#64748B]">{label}</p>
                <p className={`text-base font-semibold ${label === 'Total' ? 'text-[#0F4C81]' : label === 'Discount' ? 'text-[#DC2626]' : ''}`}>{val}</p>
              </div>
            ))}
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-xs text-[#64748B]">Balance</p>
              <p className={`text-base font-semibold ${invoice.balance > 0 ? 'text-[#DC2626]' : 'text-green-600'}`}>
                {formatCurrency(invoice.balance || 0)}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Additional Info */}
      <div className="grid grid-cols-3 gap-3">
        {invoice.lead_source && (
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Lead Source</p>
            <p className="text-sm font-semibold text-[#0F172A]">{invoice.lead_source}</p>
          </div>
        )}
        {invoice.salesperson_name && (
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Salesperson</p>
            <p className="text-sm font-semibold text-[#0F172A]">{invoice.salesperson_name}</p>
          </div>
        )}
        {invoice.reference_number && (
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Reference</p>
            <p className="text-sm font-semibold text-[#0F172A]">{invoice.reference_number}</p>
          </div>
        )}
      </div>

      {/* Customer Info */}
      {customer && (
        <div className="border border-[#E2E8F0] rounded-lg p-4">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
            <User size={16} className="text-[#0F4C81]" /> Customer Details
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-[#64748B]">Name:</span>
              <span className="ml-2 font-medium">{customer.contact_name || invoice.customer_name}</span>
            </div>
            {customer.company_name && (
              <div>
                <span className="text-[#64748B]">Company:</span>
                <span className="ml-2">{customer.company_name}</span>
              </div>
            )}
            {customer.email && (
              <div>
                <span className="text-[#64748B]">Email:</span>
                <span className="ml-2">{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div>
                <span className="text-[#64748B]">Phone:</span>
                <span className="ml-2">{customer.phone}</span>
              </div>
            )}
            {customer.gstin && (
              <div>
                <span className="text-[#64748B]">GSTIN:</span>
                <span className="ml-2 font-mono text-xs">{customer.gstin}</span>
              </div>
            )}
            {customer.place_of_supply && (
              <div data-testid="invoice-customer-place-of-supply">
                <span className="text-[#64748B]">Place of Supply:</span>
                <span className="ml-2">{customer.place_of_supply}</span>
              </div>
            )}
            {customer.outstanding !== undefined && (
              <div>
                <span className="text-[#64748B]">Total Outstanding:</span>
                <span className="ml-2 font-medium text-[#DC2626]">{formatCurrency(customer.outstanding)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Line Items */}
      {invoice.line_items && invoice.line_items.length > 0 && (
        <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="p-3 bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Package size={16} className="text-[#0F4C81]" /> Line Items
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0]">
                <th className="text-left p-3 text-[#64748B] font-medium">Item</th>
                <th className="text-left p-3 text-[#64748B] font-medium">HSN/SAC</th>
                <th className="text-right p-3 text-[#64748B] font-medium">Qty</th>
                <th className="text-right p-3 text-[#64748B] font-medium">Rate</th>
                <th className="text-right p-3 text-[#64748B] font-medium">Discount</th>
                <th className="text-right p-3 text-[#64748B] font-medium">Amount</th>
                <th className="text-left p-3 text-[#64748B] font-medium">Serial #</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.map((item, i) => (
                <tr key={i} className="border-b border-[#E2E8F0] last:border-0">
                  <td className="p-3">
                    <p className="font-medium">{item.name || item.product || 'Item'}</p>
                    {item.description && <p className="text-xs text-[#64748B] mt-0.5">{item.description}</p>}
                  </td>
                  <td className="p-3 text-xs font-mono text-[#64748B]">{item.hsn_sac || '-'}</td>
                  <td className="p-3 text-right">{item.quantity || 1}</td>
                  <td className="p-3 text-right">{formatCurrency(item.rate || item.price || 0)}</td>
                  <td className="p-3 text-right text-[#DC2626]">{item.discount ? `- ${formatCurrency(item.discount)}` : '-'}</td>
                  <td className="p-3 text-right font-medium">{formatCurrency(
                    item.amount ?? item.item_total ?? (
                      (Number(item.rate) || 0) * (Number(item.quantity) || 0) - (Number(item.discount) || 0)
                    )
                  )}</td>
                  <td className="p-3">
                    {(item.serial_numbers && item.serial_numbers.length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {item.serial_numbers.map(sn => (
                          <span key={sn} className="inline-flex items-center gap-0.5">
                            <SerialHistoryLink serialNumber={sn} className="bg-[#E8F5E9] text-[#0F4C81]" />
                            <button type="button" title="Change serial" onClick={() => setSerialTarget({ index: i, item, serial: sn })}
                              className="p-0.5 text-[#64748B] hover:text-[#0F4C81]" data-testid={`change-serial-btn-${sn}`}>
                              <PencilSimple size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-[#9CA3AF]">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChangeSerialDialog target={serialTarget} invoice={invoice} onClose={() => setSerialTarget(null)} onChanged={() => onPaymentRecorded && onPaymentRecorded()} />

      {/* Payments */}
      {payments && payments.length > 0 && (
        <div className="border border-[#E2E8F0] rounded-lg p-4">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
            <CurrencyInr size={16} className="text-[#0F4C81]" /> Payments ({payments.length})
          </h3>
          <div className="space-y-2">
            {payments.map((pmt, i) => (
              <div key={i} onClick={() => setSelectedPayment(pmt)} className="flex items-center justify-between py-2 border-b border-[#E2E8F0] last:border-0 text-sm cursor-pointer hover:bg-[#F8FAFC] rounded -mx-2 px-2 transition-colors" data-testid={`invoice-payment-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#0F4C81]">{pmt.payment_number}</span>
                    <span className="text-[#64748B] text-xs">{formatDate(pmt.payment_date)}</span>
                    {(pmt.payment_mode || pmt.mode) && <span className="text-[10px] bg-[#E2E8F0] px-1.5 py-0.5 rounded capitalize">{(pmt.payment_mode || pmt.mode).replace(/_/g, ' ')}</span>}
                    {(pmt.account_name || pmt.deposit_to) && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{pmt.account_name || pmt.deposit_to}</span>}
                  </div>
                  {(pmt.reference_number || pmt.reference || pmt.created_by) && (
                    <div className="text-[10px] text-[#64748B] mt-0.5">
                      {pmt.reference_number || pmt.reference ? <>Ref: <span className="font-mono">{pmt.reference_number || pmt.reference}</span></> : null}
                      {pmt.created_by && <span className="ml-2">by {pmt.created_by}</span>}
                    </div>
                  )}
                </div>
                <span className="font-medium text-green-600 ml-3 flex-shrink-0">{formatCurrency(pmt.amount)}</span>
              </div>
            ))}
          </div>
          <PaymentDetailDialog payment={selectedPayment} open={!!selectedPayment} onClose={() => setSelectedPayment(null)} />
        </div>
      )}

      {/* Linked Credit Notes */}
      {credit_notes && credit_notes.length > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4" data-testid="invoice-linked-cns">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-3 text-red-700">
            <CurrencyInr size={16} /> Credit Notes Linked ({credit_notes.length})
          </h3>
          <div className="space-y-2">
            {credit_notes.map((cn, i) => (
              <div key={i} onClick={() => setSelectedCN(cn)} className="flex items-center justify-between py-2 border-b border-red-200 last:border-0 text-sm cursor-pointer hover:bg-red-100 rounded -mx-2 px-2 transition-colors" data-testid={`invoice-cn-row-${i}`}>
                <div>
                  <span className="font-medium text-red-700">{cn.cn_number || cn.creditnote_number}</span>
                  <span className="text-[#64748B] text-xs ml-2">{formatDate(cn.cn_date || cn.date)}</span>
                  <span className={`text-[10px] ml-2 px-1.5 py-0.5 rounded ${cn.status === 'closed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{cn.status}</span>
                </div>
                <span className="font-bold text-red-700">{formatCurrency(cn.grand_total || cn.total || 0)}</span>
              </div>
            ))}
          </div>
          <CreditNoteDetailDialog cn={selectedCN} open={!!selectedCN} onClose={() => setSelectedCN(null)} />
        </div>
      )}

      {/* Journal Entries */}
      {journal_entries && journal_entries.length > 0 && (
        <div className="border border-[#E2E8F0] rounded-lg p-4">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
            <Receipt size={16} className="text-[#0F4C81]" /> Journal Entries ({journal_entries.length})
          </h3>
          <div className="space-y-2 text-sm">
            {journal_entries.map((je, i) => (
              <div key={i} className="py-2 border-b border-[#E2E8F0] last:border-0">
                <div className="flex justify-between">
                  <span className="font-medium">{je.entry_number}</span>
                  <span className="text-[#64748B]">{formatDate(je.date)}</span>
                </div>
                <p className="text-xs text-[#64748B] mt-0.5">{je.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GST / Tax Info */}
      {(invoice.place_of_supply || invoice.gst_treatment || invoice.customer_gstin || invoice.clinic_gstin) && (
        <div className="border border-[#E2E8F0] rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Tax Information</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {invoice.clinic_gstin && (
              <div data-testid="invoice-detail-clinic-gstin">
                <span className="text-[#64748B]">Clinic GSTIN:</span>
                <span className="ml-2 font-mono text-xs break-all">{invoice.clinic_gstin}</span>
              </div>
            )}
            {invoice.place_of_supply && (
              <div data-testid="invoice-detail-place-of-supply">
                <span className="text-[#64748B]">Place of Supply:</span>
                <span className="ml-2">{invoice.place_of_supply}</span>
              </div>
            )}
            <div>
              <span className="text-[#64748B]">GST Type:</span>
              <span className="ml-2">{invoice.is_inter_state ? 'IGST (Inter-state)' : 'CGST + SGST (Intra-state)'}</span>
            </div>
            {invoice.gst_treatment && (
              <div>
                <span className="text-[#64748B]">GST Treatment:</span>
                <span className="ml-2">{invoice.gst_treatment}</span>
              </div>
            )}
            {invoice.customer_gstin && (
              <div>
                <span className="text-[#64748B]">Customer GSTIN:</span>
                <span className="ml-2 font-mono text-xs break-all">{invoice.customer_gstin}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {showEditForm && (
        <EditInvoiceDialog
          invoice={invoice}
          customer={customer}
          onClose={() => setShowEditForm(false)}
          onSaved={() => { setShowEditForm(false); if (onPaymentRecorded) onPaymentRecorded(); }}
        />
      )}
      <Dialog open={showCNForm} onOpenChange={setShowCNForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-cn-dialog">
          <DialogHeader>
            <DialogTitle>Create Credit Note — {invoice.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#64748B]">Untick items that are not being returned, or change the quantity, rate, discount and serials. Serials on the credit note go back to stock.</p>
            <CreditNoteLinesEditor lines={cnLines} setLine={setCnLine} />
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Credit Note Date</label>
              <Input type="date" value={cnData.cn_date} onChange={e => setCnData(d => ({ ...d, cn_date: e.target.value }))} data-testid="cn-date-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Reason</label>
              <Input value={cnData.reason} onChange={e => setCnData(d => ({ ...d, reason: e.target.value }))} placeholder="Reason for credit note" data-testid="cn-reason-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Notes (optional)</label>
              <Input value={cnData.notes} onChange={e => setCnData(d => ({ ...d, notes: e.target.value }))} placeholder="Additional notes" data-testid="cn-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCNForm(false)} data-testid="cn-cancel-btn">Cancel</Button>
            <Button className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={handleCreateCreditNote} disabled={cnSaving} data-testid="cn-submit-btn">
              {cnSaving ? 'Creating...' : 'Create Credit Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Edit Invoice Dialog ────────────────────────────────── */
function EditInvoiceDialog({ invoice, customer, onClose, onSaved }) {
  const addrToStr = (a) => {
    if (!a) return '';
    if (typeof a === 'string') {
      const s = a.trim();
      if (s.startsWith('{') && s.endsWith('}')) {
        try { a = JSON.parse(s.replace(/'/g, '"')); } catch { return ''; }
      } else {
        return s;
      }
    }
    if (typeof a === 'object') {
      return [a.attention, a.address, a.street, a.street2, a.city, a.state, a.zip || a.zip_code || a.pincode, a.country]
        .filter(Boolean).join(', ');
    }
    return '';
  };
  const [form, setForm] = useState({
    customer_name: customer?.contact_name || invoice.customer_name || '',
    customer_phone: customer?.phone || invoice.customer_phone || '',
    customer_address: addrToStr(customer?.billing_address) || addrToStr(customer?.address) || addrToStr(invoice.customer_address) || '',
    customer_gstin: customer?.gstin || invoice.customer_gstin || '',
    date: invoice.invoice_date || invoice.date || new Date().toISOString().split('T')[0],
    due_date: invoice.due_date || invoice.invoice_date || invoice.date || '',
    salesperson_name: (typeof invoice.audiologist === 'string' ? invoice.audiologist : invoice.audiologist?.name) || invoice.salesperson_name || '',
    lead_source: typeof invoice.lead_source === 'string' ? invoice.lead_source : (invoice.lead_source?.name || ''),
    visit_type: invoice.visit_type || '',
    invoice_type: invoice.invoice_type || '',
    payment_mode: invoice.payment_mode || '',
    notes: invoice.notes || '',
    reference_number: invoice.reference_number || '',
    location_id: invoice.clinic_id || invoice.location_id || '',
  });
  const [lineItems, setLineItems] = useState(
    (invoice.line_items || []).map(li => ({
      item_id: li.item_id || '',
      name: li.name || li.product || '',
      hsn_sac: li.hsn_sac || '',
      quantity: li.quantity || 1,
      rate: li.rate || li.price || 0,
      discount: Number(li.discount || li.discount_amount || 0),
      tax_percent: Number(li.tax_percent || li.gst_pct || 0),
      serial_numbers: li.serial_numbers || [],
    }))
  );
  const [saving, setSaving] = useState(false);
  const [audiologists, setAudiologists] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    soCreateAPI.branches().then(r => setBranches(r.data.branches || [])).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      const [audRes, itemsRes] = await Promise.allSettled([
        soCreateAPI.audiologists(),
        soCreateAPI.items({ per_page: 2000 }),
      ]);
      if (audRes.status === 'fulfilled') setAudiologists(audRes.value.data.audiologists || []);
      if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
    })();
  }, []);

  const selectItemForLine = (idx, item) => {
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || item.gst_rate || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li,
      name: item.name,
      hsn_sac: item.hsn_sac || item.hsn_code || li.hsn_sac || '',
      rate: item.selling_price || item.sales_rate || item.rate || li.rate || 0,
      tax_percent: gstRate || li.tax_percent || 0,
      item_id: item.id,
    } : li));
  };

  const updateLine = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };
  const addLine = () => setLineItems(prev => [...prev, { name: '', hsn_sac: '', quantity: 1, rate: 0, discount: 0, tax_percent: 18, serial_numbers: [] }]);
  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const lineAmount = (li) => Math.max(Number(li.quantity || 0) * Number(li.rate || 0) - Number(li.discount || 0), 0);
  const subtotal = lineItems.reduce((s, li) => s + Number(li.quantity || 0) * Number(li.rate || 0), 0);
  const totalDiscount = lineItems.reduce((s, li) => s + Number(li.discount || 0), 0);
  const totalTax = lineItems.reduce((s, li) => s + lineAmount(li) * Number(li.tax_percent || 0) / 100, 0);
  const grandTotal = lineItems.reduce((s, li) => s + lineAmount(li), 0) + totalTax;

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    setSaving(true);
    try {
      await invoicesAPI.update(invoice.id || invoice.invoice_id, {
        ...form,
        invoice_date: form.date,
        line_items: lineItems.map(li => ({
          ...li,
          quantity: Number(li.quantity || 1),
          rate: Number(li.rate || 0),
          discount: Number(li.discount || 0),
          tax_percent: Number(li.tax_percent || 0),
        })),
      });
      toast.success(`Invoice ${invoice.invoice_number} updated`);
      onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to update invoice');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Edit Invoice — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Customer Name *</label>
            <Input data-testid="edit-customer-name" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Customer Phone</label>
            <Input data-testid="edit-customer-phone" value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#64748B] block mb-1">Customer Address</label>
            <Input data-testid="edit-customer-address" value={form.customer_address} onChange={e => setForm({...form, customer_address: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Customer GSTIN</label>
            <Input value={form.customer_gstin} onChange={e => setForm({...form, customer_gstin: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Branch</label>
            <Select value={form.location_id || ''} onValueChange={v => setForm({...form, location_id: v})}>
              <SelectTrigger data-testid="edit-invoice-branch"><SelectValue placeholder={invoice.clinic_name || 'Select branch...'} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {form.location_id && !branches.some(b => b.id === form.location_id) && (
                  <SelectItem value={form.location_id}>{invoice.clinic_name || form.location_id}</SelectItem>
                )}
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Invoice Date</label>
            <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Due Date</label>
            <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Audiologist / Salesperson</label>
            <Select value={form.salesperson_name || ''} onValueChange={v => setForm({...form, salesperson_name: v})}>
              <SelectTrigger data-testid="edit-invoice-audiologist"><SelectValue placeholder="Select audiologist..." /></SelectTrigger>
              <SelectContent className="max-h-72">
                {form.salesperson_name && !audiologists.some(a => (a.display_name || a.full_name || a.name) === form.salesperson_name) && (
                  <SelectItem value={form.salesperson_name}>{form.salesperson_name}</SelectItem>
                )}
                {audiologists.map((a, i) => {
                  const label = a.display_name || a.full_name || a.name || '';
                  return (
                    <SelectItem key={a.employee_number || label || i} value={label}>
                      {label}{a.employee_number ? ` (${a.employee_number})` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Lead Source</label>
            <Input value={form.lead_source} onChange={e => setForm({...form, lead_source: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Invoice Type</label>
            <Select value={form.invoice_type || ''} onValueChange={v => setForm({...form, invoice_type: v})}>
              <SelectTrigger data-testid="edit-invoice-type"><SelectValue placeholder="Select invoice type..." /></SelectTrigger>
              <SelectContent>
                {[...new Set([...INVOICE_TYPES, ...(invoice.invoice_type ? [invoice.invoice_type] : [])])].map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Visit Type</label>
            <Input value={form.visit_type} onChange={e => setForm({...form, visit_type: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Payment Mode</label>
            <Input value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Reference Number</label>
            <Input value={form.reference_number} onChange={e => setForm({...form, reference_number: e.target.value})} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#64748B] block mb-1">Notes</label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Line Items</h3>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">+ Add Line</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-xs text-[#64748B]">
                <tr>
                  <th className="p-2 text-left">Item</th>
                  <th className="p-2 text-center w-20">HSN</th>
                  <th className="p-2 text-right w-16">Qty</th>
                  <th className="p-2 text-right w-24">Rate</th>
                  <th className="p-2 text-right w-24">Discount</th>
                  <th className="p-2 text-right w-16">GST%</th>
                  <th className="p-2 text-right w-24">Amount</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1">
                      <ItemSearchPicker items={catalogItems} value={li.name}
                        onType={(txt) => updateLine(i, 'name', txt)}
                        onPick={(item) => selectItemForLine(i, item)}
                        testid={`edit-inv-item-${i}`} placeholder="Search item..." />
                    </td>
                    <td className="p-1"><Input value={li.hsn_sac} onChange={e => updateLine(i, 'hsn_sac', e.target.value)} className="h-8 text-xs text-center" /></td>
                    <td className="p-1"><Input type="number" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} className="h-8 text-right" /></td>
                    <td className="p-1"><Input type="number" value={li.rate} onChange={e => updateLine(i, 'rate', e.target.value)} className="h-8 text-right" /></td>
                    <td className="p-1"><Input type="number" value={li.discount} onChange={e => updateLine(i, 'discount', e.target.value)} className="h-8 text-right" /></td>
                    <td className="p-1"><Input type="number" value={li.tax_percent} onChange={e => updateLine(i, 'tax_percent', e.target.value)} className="h-8 text-right" /></td>
                    <td className="p-1 text-right font-medium">₹{lineAmount(li).toLocaleString('en-IN')}</td>
                    <td className="p-1 text-center"><button onClick={() => removeLine(i)} className="text-red-500 text-xs">×</button></td>
                  </tr>
                ))}
                {lineItems.length === 0 && <tr><td colSpan="8" className="p-4 text-center text-gray-400 text-sm">No line items</td></tr>}
              </tbody>
              <tfoot className="bg-[#F8FAFC] font-medium">
                <tr><td colSpan="6" className="p-2 text-right">Sub Total</td><td className="p-2 text-right">₹{subtotal.toLocaleString('en-IN')}</td><td></td></tr>
                <tr><td colSpan="6" className="p-2 text-right">Total Discount</td><td className="p-2 text-right text-red-600">₹{totalDiscount.toLocaleString('en-IN')}</td><td></td></tr>
                <tr><td colSpan="6" className="p-2 text-right">Tax (GST)</td><td className="p-2 text-right">₹{totalTax.toFixed(2)}</td><td></td></tr>
                <tr className="text-base"><td colSpan="6" className="p-2 text-right">Grand Total</td><td className="p-2 text-right text-[#0F4C81] font-bold">₹{grandTotal.toLocaleString('en-IN', {maximumFractionDigits: 2})}</td><td></td></tr>
              </tfoot>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F4C81] text-white" data-testid="save-invoice-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const SO_STATUS_COLORS = {
  sent: 'bg-blue-100 text-blue-800',
  placed: 'bg-indigo-100 text-indigo-800',
  in_transit: 'bg-amber-100 text-amber-800',
  fulfilled: 'bg-green-100 text-green-800',
  invoiced: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const NEXT_SO_STATUS = {
  sent: [{ value: 'placed', label: 'Mark as Placed' }, { value: 'cancelled', label: 'Cancel' }],
  placed: [{ value: 'in_transit', label: 'In Transit' }, { value: 'cancelled', label: 'Cancel' }],
  in_transit: [{ value: 'fulfilled', label: 'Fulfilled' }, { value: 'cancelled', label: 'Cancel' }],
};

function AdminSalesOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ grand_total: 0, subtotal: 0, tax_total: 0, discount_total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [soLeadSources, setSoLeadSources] = useState([]);
  const [leadSourceOpts, setLeadSourceOpts] = useState([]);
  useEffect(() => { soCreateAPI.leadSources().then(r => setLeadSourceOpts((r.data.lead_sources || []).map(l => ({ value: l.name, label: l.name })))).catch(() => {}); }, []);
  const [locationIds, setLocationIds] = useState([]); // multi-select
  const [branches, setBranches] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSO, setSelectedSO] = useState(null);
  const [soDetail, setSODetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [soUploading, setSoUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingSO, setEditingSO] = useState(null);
  const [drill, setDrill] = useState(null);
  const [soExporting, setSoExporting] = useState(false);
  const soFileRef = useRef(null);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search };
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      if (locationIds.length > 0) params.location_id = locationIds.join(',');
      if (soLeadSources.length > 0) params.lead_source = soLeadSources.join(',');
      if (startDate) params.from_date = startDate;
      if (endDate) params.to_date = endDate;
      const { data } = await billingAPI.adminGetSalesOrders(params);
      setOrders(data.sales_orders || []);
      setTotal(data.total || 0);
      setTotals(data.totals || { grand_total: 0, subtotal: 0, tax_total: 0, discount_total: 0, count: 0 });
      setSelectedIds(new Set());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, locationIds, soLeadSources, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  // Quick range presets
  const setSOToday = () => { setStartDate(today); setEndDate(today); setPage(1); };
  const setSOThisMonth = () => {
    const d = new Date();
    setStartDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
    setEndDate(today);
    setPage(1);
  };
  const setSOAllTime = () => { setStartDate(''); setEndDate(''); setPage(1); };

  useEffect(() => {
    billingAPI.adminGetBranches?.().then(res => setBranches(res.data.branches || [])).catch(() => {});
  }, []);

  const handleSOBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSoUploading(true);
    try {
      const { data } = await billingAPI.adminBulkUploadSO(file);
      toast.success(`${data.created} sales orders created, ${data.skipped} skipped`);
      if (data.errors?.length) toast.error(`Errors: ${data.errors[0]}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setSoUploading(false); e.target.value = ''; }
  };

  const handleSODownloadTemplate = async () => {
    try {
      const { data } = await billingAPI.adminDownloadSOTemplate();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'so_bulk_upload_template.csv';
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleExportSOs = async () => {
    setSoExporting(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      if (locationIds.length > 0) params.location_id = locationIds.join(',');
      if (soLeadSources.length > 0) params.lead_source = soLeadSources.join(',');
      if (startDate) params.from_date = startDate;
      if (endDate) params.to_date = endDate;
      const resp = await billingAPI.adminExportSalesOrders(params);
      downloadBlobResponse(resp.data, `sales_orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Sales orders exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setSoExporting(false); }
  };

  const loadSODetail = async (soId) => {
    setSelectedSO(soId);
    setDetailLoading(true);
    try {
      const { data } = await billingAPI.adminGetSalesOrder(soId);
      setSODetail(data);
    } catch (e) { toast.error('Failed to load SO details'); }
    finally { setDetailLoading(false); }
  };

  const handleStatusUpdate = async (soId, newStatus) => {
    try {
      const user = JSON.parse(localStorage.getItem('hearclear_user') || '{}');
      await billingAPI.adminUpdateSOStatus(soId, { status: newStatus, note: '', updated_by: user.username || 'Admin' });
      toast.success(`Status updated to ${newStatus}`);
      load();
      setSelectedSO(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDeleteSO = async (soId, soNumber) => {
    if (!window.confirm(`Delete sales order ${soNumber}? This action cannot be undone.`)) return;
    try {
      await billingAPI.adminDeleteSO(soId);
      toast.success(`Sales Order ${soNumber} deleted`);
      setSelectedSO(null);
      setSODetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const [convertSO, setConvertSO] = useState(null);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length && orders.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(orders.map(o => o.id)));
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} sales order(s)? This action cannot be undone. Invoiced SOs will be skipped.`)) return;
    try {
      const { data } = await billingAPI.adminBulkDeleteSOs(Array.from(selectedIds));
      toast.success(`Deleted ${data.deleted} SO(s)${data.skipped ? `, skipped ${data.skipped} invoiced` : ''}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sales-orders-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by SO number or customer..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9" data-testid="so-search" />
        </div>
        <MultiSelectFilter value={statusFilter} onChange={v => { setStatusFilter(v); setPage(1); }}
          options={SO_STATUS_OPTIONS} allLabel="All Status" noun="statuses" testid="so-status-filter" buttonClassName="w-44" />
        <MultiSelectFilter value={locationIds} onChange={v => { setLocationIds(v); setPage(1); }}
          options={branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))} allLabel="All Locations" noun="locations" testid="so-location-filter" buttonClassName="w-56" />
        <MultiSelectFilter value={soLeadSources} onChange={v => { setSoLeadSources(v); setPage(1); }}
          options={leadSourceOpts} allLabel="All Lead Sources" noun="lead sources" testid="so-lead-source-filter" buttonClassName="w-48" />
        <p className="text-sm text-[#64748B]">
          {total} orders · Total <strong className="text-[#0B1B3D]">₹{Number(totals.grand_total || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</strong>
        </p>
      </div>

      {/* Date filter row */}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">From</label>
            <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} className="w-40 h-9" data-testid="so-from-date" />
          </div>
          <span className="text-[#64748B] mt-4">—</span>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">To</label>
            <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} className="w-40 h-9" data-testid="so-to-date" />
          </div>
        </div>
        <div className="h-6 w-px bg-[#E2E8F0]" />
        <div className="flex bg-[#F8FAFC] rounded-lg p-0.5">
          <button onClick={setSOToday} className={`px-2.5 py-1 text-xs font-medium rounded-md ${startDate === today && endDate === today ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="so-quick-today">Today</button>
          <button onClick={setSOThisMonth} className={`px-2.5 py-1 text-xs font-medium rounded-md ${startDate && startDate !== today && endDate === today ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="so-quick-month">This Month</button>
          <button onClick={setSOAllTime} className={`px-2.5 py-1 text-xs font-medium rounded-md ${!startDate && !endDate ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="so-quick-all">All Time</button>
        </div>
        <div className="ml-auto flex items-center gap-2">{/* placeholder for right-aligned future controls */}</div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        {selectedIds.size > 0 && (
          <Button variant="outline" size="sm" onClick={handleBulkDelete}
            className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-so-btn">
            <Trash size={14} className="mr-1" /> Delete Selected ({selectedIds.size})
          </Button>
        )}
        <input type="file" accept=".csv,.xlsx" ref={soFileRef} onChange={handleSOBulkUpload} className="hidden" />
        <Button className="bg-[#0F4C81] hover:bg-[#0B3C68] text-white" size="sm" onClick={handleExportSOs} disabled={soExporting} data-testid="export-so-btn">
          <Export size={14} className="mr-1" /> {soExporting ? 'Exporting…' : 'Export'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => soFileRef.current?.click()} disabled={soUploading} data-testid="bulk-upload-so-btn">
          <Upload size={14} className="mr-1" /> {soUploading ? 'Uploading...' : 'Bulk Upload'}
        </Button>
        <Button variant="outline" size="sm" onClick={handleSODownloadTemplate} data-testid="sample-file-so-btn">
          <DownloadSimple size={14} className="mr-1" /> Sample File
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-so-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
              <th className="p-3 w-8">
                <input type="checkbox" checked={selectedIds.size === orders.length && orders.length > 0}
                  onChange={toggleSelectAll} className="cursor-pointer" data-testid="so-select-all" />
              </th>
              <th className="text-left p-3">SO Number</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Items</th>
              <th className="text-left p-3">Location</th>
              <th className="text-left p-3">Lead Source</th>
              <th className="text-left p-3">Date</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : orders.length > 0 ? orders.map(so => {
              const nextActions = NEXT_SO_STATUS[so.status] || [];
              const customerName = so.customer_name || so.customer?.name || '-';
              const itemsList = so.items && so.items.length > 0 ? so.items : (so.line_items ? so.line_items.map(li => li.name) : []);
              const locationName = so.location_name || so.clinic_name || '-';
              return (
                <tr key={so.id} className="border-t border-gray-100 hover:bg-[#F8FAFC] transition-colors" data-testid={`so-row-${so.so_number}`}>
                  <td className="p-3">
                    <input type="checkbox" checked={selectedIds.has(so.id)} onChange={() => toggleSelect(so.id)}
                      className="cursor-pointer" data-testid={`so-select-${so.id}`} />
                  </td>
                  <td className="p-3 font-medium text-[#0B1B3D]">{so.so_number}</td>
                  <td className="p-3 max-w-[160px]">
                    {customerName !== '-' ? (
                      <button onClick={() => setDrill({ id: so.customer_id || so.customer?.id, name: customerName, phone: so.customer_phone || so.customer?.phone })} className="truncate font-medium text-[#0B1B3D] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left block w-full" title={customerName} data-testid={`so-customer-${so.so_number}`}>{customerName}</button>
                    ) : <div className="truncate font-medium">-</div>}
                  </td>
                  <td className="p-3 max-w-[180px]">
                    {itemsList.length > 0 ? (
                      <div className="space-y-0.5">
                        {itemsList.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="text-xs truncate" title={typeof item === 'string' ? item : item?.name || ''}>{typeof item === 'string' ? item : item?.name || ''}</div>
                        ))}
                        {itemsList.length > 2 && <span className="text-[10px] text-[#64748B]">+{itemsList.length - 2} more</span>}
                      </div>
                    ) : so.quantity ? (
                      <span className="text-xs text-[#64748B]">{so.quantity} item{so.quantity !== 1 ? 's' : ''}</span>
                    ) : <span className="text-xs text-[#64748B]">-</span>}
                  </td>
                  <td className="p-3 text-[#64748B] text-xs max-w-[140px]">
                    <div className="truncate" title={locationName}>{locationName}</div>
                  </td>
                  <td className="p-3 text-[#64748B] text-xs">{so.lead_source || '-'}</td>
                  <td className="p-3 text-[#64748B]">{so.date}</td>
                  <td className="p-3 text-right font-medium">{formatCurrency(so.grand_total)}</td>
                  <td className="p-3 text-center">
                    <Badge className={`text-xs ${SO_STATUS_COLORS[so.status] || 'bg-gray-100'}`}>{so.status}</Badge>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => loadSODetail(so.id)} data-testid={`view-so-${so.id}`}>
                        <Eye size={16} />
                      </Button>
                      {so.status !== 'invoiced' && so.status !== 'cancelled' && (
                        <Button variant="ghost" size="sm" onClick={() => setEditingSO(so)} data-testid={`edit-so-${so.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                      )}
                      {nextActions.length > 0 && nextActions.map(a => (
                        <Button key={a.value} variant="outline" size="sm"
                          className={`text-xs h-7 rounded-lg ${a.value === 'cancelled' ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-[#0B1B3D] border-[#0B1B3D]/20 hover:bg-[#0B1B3D]/5'}`}
                          onClick={() => handleStatusUpdate(so.id, a.value)}
                          data-testid={`so-action-${a.value}-${so.id}`}>
                          {a.label}
                        </Button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan={10} className="text-center py-8 text-[#64748B]">No sales orders found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-[#64748B]">Page {page} of {Math.ceil(total / pageSize)}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* SO Detail Dialog */}
      <Dialog open={!!selectedSO} onOpenChange={o => { if (!o) { setSelectedSO(null); setSODetail(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-so-detail-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Sales Order Details</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" /></div>
          ) : soDetail ? (() => {
            const liArr = soDetail.line_items || [];
            const totalQty = liArr.reduce((s, li) => s + (li.quantity_ordered ?? li.quantity ?? 0), 0);
            const totalInv = liArr.reduce((s, li) => s + (li.quantity_invoiced ?? 0), 0);
            const invStatus = soDetail.invoiced_status || (totalInv >= totalQty && totalQty > 0 ? 'invoiced' : (totalInv > 0 ? 'partially invoiced' : 'not invoiced'));
            const salesPerson = soDetail.sales_person || soDetail.salesperson_name || soDetail.salesperson;
            return (
            <div className="space-y-4">
              {/* Header with SO Number and Status */}
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#0B1B3D]">{soDetail.so_number || soDetail.salesorder_number}</h2>
                  <p className="text-sm text-[#64748B]">Date: {soDetail.date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${SO_STATUS_COLORS[soDetail.status] || 'bg-gray-100'}`}>{soDetail.status || soDetail.order_status}</Badge>
                  {!soDetail.converted_to_invoice_id && !['invoiced', 'cancelled'].includes(soDetail.status) && (
                    <Button variant="outline" size="sm" onClick={() => setConvertSO(soDetail)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="convert-so-btn">
                      <ArrowRight size={14} className="mr-1" /> Convert to Invoice
                    </Button>
                  )}
                  {soDetail.converted_to_invoice_number && (
                    <span className="text-xs text-emerald-600 font-medium">Inv: {soDetail.converted_to_invoice_number}</span>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleDeleteSO(soDetail.id, soDetail.so_number)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-so-btn">
                    <Trash size={14} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Customer</p>
                  <p className="font-semibold text-[#0B1B3D]">{soDetail.customer_name || soDetail.customer?.name || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Location</p>
                  <p className="font-semibold text-[#0B1B3D]">{soDetail.location_name || soDetail.clinic_name || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Salesperson</p>
                  <p className="font-semibold text-[#0B1B3D]" data-testid="admin-so-salesperson">{salesPerson || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Lead Source</p>
                  <p className="font-semibold text-[#0B1B3D]">{soDetail.lead_source || soDetail.source || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Quantity</p>
                  <p className="font-semibold text-[#0B1B3D]">{totalQty || soDetail.quantity || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Invoiced Status</p>
                  <p className="font-semibold text-[#0B1B3D] capitalize">
                    {invStatus}
                    {totalQty > 0 && <span className="text-[10px] text-[#64748B] ml-1 font-normal">({totalInv}/{totalQty} invoiced)</span>}
                  </p>
                </div>
              </div>

              {/* Line items — full columns */}
              {liArr.length > 0 && (
                <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <div className="p-3 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <h3 className="text-sm font-medium text-[#0B1B3D]">Line Items ({liArr.length})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-white text-[#64748B] border-b border-[#E2E8F0]">
                          <th className="text-left p-2 font-medium">Item</th>
                          <th className="text-left p-2 font-medium">HSN</th>
                          <th className="text-right p-2 font-medium">Qty</th>
                          <th className="text-right p-2 font-medium">Rate</th>
                          <th className="text-right p-2 font-medium">Disc</th>
                          <th className="text-right p-2 font-medium">Tax%</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-left p-2 font-medium">Warehouse</th>
                          <th className="text-left p-2 font-medium">Serials</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liArr.map((li, i) => (
                          <tr key={i} className="border-t border-[#E2E8F0]">
                            <td className="p-2 font-medium">{li.name || '-'}</td>
                            <td className="p-2 text-[#64748B] font-mono">{li.hsn_sac || li.hsn || '-'}</td>
                            <td className="p-2 text-right">{li.quantity ?? li.quantity_ordered ?? '-'}</td>
                            <td className="p-2 text-right">{formatCurrency(li.rate || 0)}</td>
                            <td className="p-2 text-right text-[#64748B]">{(li.discount_amount || li.discount) ? formatCurrency(li.discount_amount || li.discount) : '-'}</td>
                            <td className="p-2 text-right text-[#64748B]">{li.tax_percent ? `${li.tax_percent}%` : '-'}</td>
                            <td className="p-2 text-right font-medium">{formatCurrency(li.amount || li.line_amount || li.item_total || li.total || 0)}</td>
                            <td className="p-2 text-[#64748B] text-[10px]">{li.line_item_location_name || li.warehouse_name || '-'}</td>
                            <td className="p-2">{(li.serial_numbers || []).length > 0 ? (li.serial_numbers || []).map(sn => (
                              <span key={sn} className="text-[10px] bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded font-mono mr-1">{sn}</span>
                            )) : <span className="text-[#64748B]">-</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Grand Total */}
              <div className="bg-[#0F4C81] rounded-lg p-4">
                {totalQty > 0 && (
                  <div className="flex justify-between text-sm text-emerald-200 mb-1">
                    <span>Quantity</span><span>{totalQty}</span>
                  </div>
                )}
                {soDetail.total_invoiced_amount > 0 && (
                  <div className="flex justify-between text-sm text-emerald-200 mb-1">
                    <span>Invoiced Amount</span><span>{formatCurrency(soDetail.total_invoiced_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-white border-t border-emerald-700 mt-1 pt-2">
                  <span>Grand Total</span><span>{formatCurrency(soDetail.grand_total || 0)}</span>
                </div>
              </div>

              {/* Linked Invoices + serials allocated (closes SO → Invoice → Serial loop) */}
              {soDetail.linked_invoices?.length > 0 && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3" data-testid="so-linked-invoices">
                  <p className="text-xs font-medium text-blue-800 mb-2">
                    Linked Invoices ({soDetail.linked_invoices.length}) — serials allocated when this SO was invoiced
                  </p>
                  <div className="space-y-1">
                    {soDetail.linked_invoices.map((inv, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-white border border-blue-100 rounded px-2 py-1">
                        <span className="font-mono text-[#0F4C81]">{inv.invoice_number}</span>
                        <span className="text-[#64748B]">{inv.date}</span>
                        <span className="font-medium">{formatCurrency(inv.grand_total || 0)}</span>
                        <span className="text-blue-700 text-[10px]">{inv.serial_count} serial{inv.serial_count === 1 ? '' : 's'}</span>
                      </div>
                    ))}
                  </div>
                  {soDetail.linked_invoice_serials?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <p className="text-[10px] uppercase tracking-wide text-blue-700 mb-1">All Serials Allocated</p>
                      <div className="flex flex-wrap gap-1">
                        {soDetail.linked_invoice_serials.map((s, i) => (
                          <span key={i} className="text-[10px] bg-white border border-blue-200 rounded px-1.5 py-0.5">
                            <span className="font-mono text-blue-800">{s.serial_number}</span>
                            <span className="text-[#64748B]"> — {s.item_name}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {soDetail.status_history?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Status History</p>
                  <div className="space-y-1">
                    {soDetail.status_history.map((h, i) => (
                      <div key={i} className="flex justify-between items-center text-xs border-b border-gray-50 py-1.5">
                        <div className="flex items-center gap-2">
                          <Badge className={`${SO_STATUS_COLORS[h.status] || 'bg-gray-100'} text-[10px]`}>{h.status}</Badge>
                          {h.updated_by && <span className="text-[#0B1B3D] font-medium">by {h.updated_by}</span>}
                        </div>
                        <span className="text-gray-400">{h.timestamp?.slice(0, 16)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })() : null}
        </DialogContent>
      </Dialog>

      {/* Edit SO Dialog */}
      <EditSODialog so={editingSO} branches={branches} onClose={() => setEditingSO(null)} onSaved={() => { setEditingSO(null); load(); }} />
      <AdminConvertSODialog so={convertSO} onClose={() => setConvertSO(null)} onConverted={() => { setConvertSO(null); setSelectedSO(null); setSODetail(null); load(); }} />
      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} customerPhone={drill?.phone || ''} />
    </div>
  );
}

/* ─── Admin serial multi-picker (from a branch's available stock) ─── */
function AdminSerialMultiSelect({ itemId, itemName, location, value = [], max = 1, onChange, testid }) {
  const [serials, setSerials] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    if (!itemId && !itemName) { setSerials([]); return; }
    soCreateAPI.availableSerials({ item_id: itemId || '', item_name: itemName || '', location: location || '' })
      .then(({ data }) => { if (active) setSerials(data.serial_numbers || []); })
      .catch(() => { if (active) setSerials([]); });
    return () => { active = false; };
  }, [itemId, itemName, location]);

  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const options = [...new Set([...selected, ...serials])];
  const cap = Math.max(1, Number(max) || 1);
  const toggle = (sn) => {
    if (selected.includes(sn)) onChange(selected.filter(s => s !== sn));
    else if (selected.length < cap) onChange([...selected, sn]);
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full h-8 border border-gray-200 rounded-lg px-2 text-xs bg-white text-left flex items-center justify-between hover:border-[#0F4C81]"
        data-testid={testid}>
        <span className={selected.length ? 'text-[#0B1B3D] truncate' : 'text-gray-400'}>
          {selected.length ? `${selected.length}/${cap} selected` : (options.length ? 'Select serial…' : 'No stock serials')}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" className={`transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}><path d="M1 1l4 4 4-4" stroke="#64748B" strokeWidth="1.5" fill="none" /></svg>
      </button>
      {open && options.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-44 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {options.map(sn => {
            const checked = selected.includes(sn);
            const disabled = !checked && selected.length >= cap;
            return (
              <label key={sn} className={`flex items-center gap-2 px-2 py-1.5 text-xs ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'} ${checked ? 'bg-emerald-50' : ''}`}>
                <input type="checkbox" checked={checked} disabled={disabled}
                  onMouseDown={e => { e.preventDefault(); if (!disabled) toggle(sn); }}
                  onChange={() => {}} className="h-3.5 w-3.5" />
                <span className="font-mono">{sn}</span>
              </label>
            );
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map(sn => (
            <span key={sn} className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono">
              {sn}
              <button type="button" onMouseDown={e => { e.preventDefault(); toggle(sn); }} className="text-gray-400 hover:text-red-500 ml-0.5">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Admin Convert SO → Invoice (serial selection, mirrors clinic flow) ─── */
function AdminConvertSODialog({ so, onClose, onConverted }) {
  const [lineItems, setLineItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fill, setFill] = useState({});
  useEffect(() => { setFill({}); }, [so?.id]);
  useEffect(() => {
    if (so) setLineItems((so.line_items || []).map(li => ({ ...li, serial_numbers: [] })));
  }, [so]);
  if (!so) return null;

  const location = so.clinic_name || so.location_name || so.from_location_name || '';
  const updateSN = (idx, vals) => setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, serial_numbers: vals } : li));

  const handleConvert = async () => {
    const err = soCoreError(so, fill);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await salesCreditsAPI.convertSOToInvoice(so.id, { line_items: lineItems, payment_mode: 'cash', notes: so.notes || '', ...fill });
      toast.success(`Invoice ${data.invoice_number || ''} created from ${so.so_number || ''}`);
      onConverted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Conversion failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!so} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-convert-so-dialog">
        <DialogHeader>
          <DialogTitle>Convert {so.so_number} to Invoice</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">Select serial numbers from available stock at <strong>{location || 'this branch'}</strong>, then confirm. The invoice keeps the SO's amounts &amp; discounts. Payment can be recorded later on the invoice.</p>
        <SOCoreFieldsPanel so={so} fill={fill} setFill={setFill}
          loadAudiologists={soCreateAPI.audiologists} loadLeadSources={soCreateAPI.leadSources} />
        <div className="space-y-3 mt-3">
          {lineItems.map((li, idx) => (
            <div key={idx} className="border rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1">
                <p className="font-medium text-sm">{li.name}</p>
                <p className="text-xs text-gray-400">Qty: {li.quantity} | Rate: ₹{Number(li.rate || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="w-52">
                <label className="text-[10px] text-gray-500">Serial Number{Number(li.quantity) > 1 ? `s (${li.quantity})` : ''}</label>
                <AdminSerialMultiSelect itemId={li.item_id} itemName={li.name} location={location}
                  value={li.serial_numbers || []} max={li.quantity} onChange={vals => updateSN(idx, vals)}
                  testid={`admin-convert-sn-${idx}`} />
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-convert-cancel">Cancel</Button>
          <Button onClick={handleConvert} disabled={submitting} className="bg-[#0F4C81] text-white" data-testid="admin-convert-confirm">
            {submitting ? 'Converting...' : 'Convert to Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditSODialog({ so, branches, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [audiologists, setAudiologists] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);

  useEffect(() => {
    (async () => {
      const [audRes, itemsRes] = await Promise.allSettled([
        soCreateAPI.audiologists(),
        soCreateAPI.items({ per_page: 2000 }),
      ]);
      if (audRes.status === 'fulfilled') setAudiologists(audRes.value.data.audiologists || []);
      if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
    })();
  }, []);

  useEffect(() => {
    if (so) {
      const toStr = (v) => {
        if (!v) return '';
        if (typeof v === 'string') return v;
        if (typeof v === 'object') return v.name || v.label || v.value || '';
        return String(v);
      };
      setForm({
        customer_name: so.customer_name || so.customer?.name || '',
        customer_phone: so.customer_phone || so.customer?.phone || '',
        date: so.date || '',
        audiologist: toStr(so.audiologist),
        lead_source: toStr(so.lead_source),
        visit_type: so.visit_type || '',
        notes: so.notes || '',
        location_id: so.location_id || so.branch_id || so.clinic_id || '',
        line_items: (so.line_items || []).map(li => ({
          name: li.name || '',
          quantity: li.quantity ?? li.qty ?? 1,
          rate: li.rate ?? 0,
          discount: li.discount ?? li.discount_amount ?? 0,
          tax_pct: li.tax_pct ?? li.gst_pct ?? 0,
          hsn_sac: li.hsn_sac || '',
        })),
      });
    }
  }, [so]);

  if (!so) return null;

  const updateItem = (i, k, v) => {
    setForm(f => {
      const items = [...(f.line_items || [])];
      items[i] = { ...items[i], [k]: v };
      return { ...f, line_items: items };
    });
  };
  const pickItem = (i, item) => {
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || item.gst_rate || '18') : 0;
    setForm(f => {
      const items = [...(f.line_items || [])];
      items[i] = {
        ...items[i],
        name: item.name,
        hsn_sac: item.hsn_sac || item.hsn_code || items[i].hsn_sac || '',
        rate: item.selling_price || item.sales_rate || item.rate || items[i].rate || 0,
        tax_pct: gstRate || items[i].tax_pct || 0,
        item_id: item.id,
      };
      return { ...f, line_items: items };
    });
  };
  const addItem = () => setForm(f => ({ ...f, line_items: [...(f.line_items || []), { name: '', quantity: 1, rate: 0, discount: 0, tax_pct: 18, hsn_sac: '' }] }));
  const removeItem = (i) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.customer_name) { toast.error('Customer name required'); return; }
    setSaving(true);
    try {
      await billingAPI.adminEditSO(so.id, form);
      toast.success('Sales Order updated');
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : 'Update failed';
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!so} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-so-dialog">
        <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Edit Sales Order — {so.so_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Customer Name *</label>
              <Input value={form.customer_name || ''} onChange={e => setForm(f => ({...f, customer_name: e.target.value}))} data-testid="edit-so-customer-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Phone</label>
              <Input value={form.customer_phone || ''} onChange={e => setForm(f => ({...f, customer_phone: e.target.value}))} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Date</label>
              <Input type="date" value={form.date || ''} onChange={e => setForm(f => ({...f, date: e.target.value}))} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Location</label>
              <SearchableSelect value={form.location_id || ''} onChange={v => setForm(f => ({...f, location_id: v}))} placeholder="Select location" testid="edit-so-location" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Audiologist</label>
              <Select value={form.audiologist || ''} onValueChange={v => setForm(f => ({...f, audiologist: v}))}>
                <SelectTrigger data-testid="edit-so-audiologist"><SelectValue placeholder="Select audiologist..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {form.audiologist && !audiologists.some(a => (a.display_name || a.full_name || a.name) === form.audiologist) && (
                    <SelectItem value={form.audiologist}>{form.audiologist}</SelectItem>
                  )}
                  {audiologists.map((a, i) => {
                    const label = a.display_name || a.full_name || a.name || '';
                    return (
                      <SelectItem key={a.employee_number || label || i} value={label}>
                        {label}{a.employee_number ? ` (${a.employee_number})` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Lead Source</label>
              <Input value={form.lead_source || ''} onChange={e => setForm(f => ({...f, lead_source: e.target.value}))} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-[#0B1B3D]">Line Items</label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="edit-so-add-item">+ Add Item</Button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-2">
              {(form.line_items || []).length > 0 && (
                <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold uppercase tracking-wide text-[#64748B] px-2">
                  <span className="col-span-3">Item Name</span>
                  <span className="col-span-1 text-center">Qty</span>
                  <span className="col-span-2 text-right">Rate (₹)</span>
                  <span className="col-span-2 text-right">Discount (₹)</span>
                  <span className="col-span-1 text-center">GST%</span>
                  <span className="col-span-1">HSN</span>
                  <span className="col-span-1 text-right">Amount</span>
                  <span className="col-span-1"></span>
                </div>
              )}
              {(form.line_items || []).map((li, i) => {
                const q = parseFloat(li.quantity) || 0;
                const r = parseFloat(li.rate) || 0;
                const d = parseFloat(li.discount) || 0;
                const amount = Math.max(q * r - d, 0);
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-[#F8FAFC] p-2 rounded">
                    <div className="col-span-3">
                      <ItemSearchPicker items={catalogItems} value={li.name}
                        onType={(txt) => updateItem(i, 'name', txt)}
                        onPick={(item) => pickItem(i, item)}
                        testid={`edit-so-item-${i}`} placeholder="Item name" />
                    </div>
                    <Input className="col-span-1 text-center" type="number" placeholder="Qty" value={li.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} />
                    <Input className="col-span-2 text-right" type="number" placeholder="Rate" value={li.rate} onChange={e => updateItem(i, 'rate', e.target.value)} />
                    <Input className="col-span-2 text-right" type="number" placeholder="Discount" value={li.discount} onChange={e => updateItem(i, 'discount', e.target.value)} />
                    <Input className="col-span-1 text-center" type="number" placeholder="GST%" value={li.tax_pct} onChange={e => updateItem(i, 'tax_pct', e.target.value)} />
                    <Input className="col-span-1" placeholder="HSN" value={li.hsn_sac} onChange={e => updateItem(i, 'hsn_sac', e.target.value)} />
                    <span className="col-span-1 text-right text-sm font-semibold text-[#0B1B3D]">₹{amount.toLocaleString('en-IN', {maximumFractionDigits: 2})}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="col-span-1 text-red-600"><Trash size={14} /></Button>
                  </div>
                );
              })}
              {(form.line_items || []).length === 0 && <p className="text-xs text-center text-gray-400 py-3">No line items</p>}
              {(form.line_items || []).length > 0 && (() => {
                const subtotal = (form.line_items || []).reduce((acc, li) => {
                  const q = parseFloat(li.quantity) || 0;
                  const r = parseFloat(li.rate) || 0;
                  const d = parseFloat(li.discount) || 0;
                  return acc + Math.max(q * r - d, 0);
                }, 0);
                const tax = (form.line_items || []).reduce((acc, li) => {
                  const q = parseFloat(li.quantity) || 0;
                  const r = parseFloat(li.rate) || 0;
                  const d = parseFloat(li.discount) || 0;
                  const tp = parseFloat(li.tax_pct) || 0;
                  return acc + Math.max(q * r - d, 0) * tp / 100;
                }, 0);
                return (
                  <div className="grid grid-cols-12 gap-2 pt-2 px-2 border-t border-gray-300 text-sm">
                    <span className="col-span-8 text-right text-[#64748B]">Subtotal / Tax / Total:</span>
                    <span className="col-span-3 text-right font-semibold text-[#0B1B3D]">
                      ₹{subtotal.toLocaleString('en-IN', {maximumFractionDigits: 2})} + ₹{tax.toLocaleString('en-IN', {maximumFractionDigits: 2})} = ₹{(subtotal + tax).toLocaleString('en-IN', {maximumFractionDigits: 2})}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Notes</label>
            <Input value={form.notes || ''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0B1B3D] text-white" data-testid="edit-so-save">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminCreditNotesTab() {
  const [cns, setCns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedCN, setSelectedCN] = useState(null);
  const [branches, setBranches] = useState([]);
  const [clinic, setClinic] = useState([]);
  const [cnLeadSources, setCnLeadSources] = useState([]);
  const [cnLeadOpts, setCnLeadOpts] = useState([]);
  useEffect(() => { soCreateAPI.leadSources().then(r => setCnLeadOpts((r.data.lead_sources || []).map(l => ({ value: l.name, label: l.name })))).catch(() => {}); }, []);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [editCN, setEditCN] = useState(null);
  const [cnExporting, setCnExporting] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    billingAPI.adminGetBranches?.().then(res => setBranches(res.data.branches || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await billingAPI.adminGetCreditNotes({
        search, page, page_size: pageSize,
        clinic: clinic.length ? clinic.join(',') : undefined,
        lead_source: cnLeadSources.length ? cnLeadSources.join(',') : undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      });
      setCns(data.notes || data.credit_notes || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, clinic, cnLeadSources, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const cnKey = (cn) => cn.id || cn.cn_number || cn.creditnote_number;
  const allSelected = cns.length > 0 && selectedIds.size === cns.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(cns.map(cnKey)));
  };
  const toggleOne = (k) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const nums = cns.filter(cn => selectedIds.has(cnKey(cn))).map(cn => cn.cn_number || cn.creditnote_number).filter(Boolean);
    const preview = nums.slice(0, 12).join(', ') + (nums.length > 12 ? `, +${nums.length - 12} more` : '');
    if (!window.confirm(`Delete ${selectedIds.size} credit note(s)? This cannot be undone.\n\n${preview}`)) return;
    setDeleting(true);
    try {
      const { data } = await billingAPI.adminBulkDeleteCreditNotes(Array.from(selectedIds));
      toast.success(`Deleted ${data.deleted} credit note(s)`);
      setPage(1);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete credit notes');
    } finally { setDeleting(false); }
  };

  const handleDeleteCN = async (cn) => {
    const num = cn.cn_number || cn.creditnote_number || '';
    if (!window.confirm(`Delete credit note ${num}?\n\nSerials it returned to stock go back to sold and the linked invoice is restored. This cannot be undone.`)) return;
    try {
      const { data } = await billingAPI.adminDeleteCreditNote(cn.id || num);
      toast.success(`Deleted ${data.cn_number || num}${data.serials_reverted ? ` · ${data.serials_reverted} serial(s) reverted` : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete credit note');
    }
  };

  const clearFilters = () => { setClinic([]); setCnLeadSources([]); setFromDate(''); setToDate(''); setSearch(''); setPage(1); };

  const handleExportCNs = async () => {
    setCnExporting(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (clinic.length) params.clinic = clinic.join(',');
      if (cnLeadSources.length) params.lead_source = cnLeadSources.join(',');
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const resp = await billingAPI.adminExportCreditNotes(params);
      downloadBlobResponse(resp.data, `credit_notes_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Credit notes exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setCnExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-credit-notes-tab">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search credit notes..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" data-testid="cn-admin-search" />
        </div>
        <MultiSelectFilter value={clinic} onChange={v => { setClinic(v); setPage(1); }}
          options={branches.map(b => ({ value: b.name, label: b.name }))}
          allLabel="All Locations" noun="locations" testid="cn-filter-location" buttonClassName="w-52" />
        <MultiSelectFilter value={cnLeadSources} onChange={v => { setCnLeadSources(v); setPage(1); }}
          options={cnLeadOpts} allLabel="All Lead Sources" noun="lead sources" testid="cn-filter-lead-source" buttonClassName="w-48" />
        <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }}
          className="border border-[#E2E8F0] rounded-md px-2 py-1.5 text-sm" data-testid="cn-filter-from" />
        <span className="text-[#64748B] text-sm">—</span>
        <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }}
          className="border border-[#E2E8F0] rounded-md px-2 py-1.5 text-sm" data-testid="cn-filter-to" />
        {(clinic.length > 0 || cnLeadSources.length > 0 || fromDate || toDate || search) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="cn-clear-filters">Clear</Button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <Button className="bg-[#0F4C81] hover:bg-[#0B3C68] text-white" size="sm" onClick={handleExportCNs} disabled={cnExporting} data-testid="export-cn-btn">
            <Export size={14} className="mr-1" /> {cnExporting ? 'Exporting…' : 'Export'}
          </Button>
          {selectedIds.size > 0 && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleBulkDelete} disabled={deleting} data-testid="cn-bulk-delete-btn">
              <Trash size={14} className="mr-1" /> Delete Selected ({selectedIds.size})
            </Button>
          )}
          <p className="text-sm text-[#64748B]" data-testid="cn-total-count">{total} credit notes</p>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-cn-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
              <th className="p-3 w-10 text-center">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="cn-select-all" />
              </th>
              <th className="text-left p-3">CN Number</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Clinic</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Linked Invoice</th>
              <th className="text-left p-3">Lead Source</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-right p-3">Balance</th>
              <th className="text-center p-3">Status</th>
              <th className="text-left p-3">Reason</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : cns.length > 0 ? cns.map(cn => {
              const custName = cn.customer?.name || cn.customer_name || '-';
              const linkedInv = cn.reference_invoice_number || cn.linked_invoice_number || cn.reference_number || cn.invoice_number;
              const k = cnKey(cn);
              return (
              <tr key={cn.id} className="border-t border-gray-100 hover:bg-[#F8FAFC] transition-colors"
                data-testid={`cn-admin-row-${cn.cn_number}`}>
                <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.has(k)} onChange={() => toggleOne(k)} data-testid={`cn-select-${cn.cn_number}`} />
                </td>
                <td className="p-3 font-medium text-[#0B1B3D] cursor-pointer" onClick={() => setSelectedCN(cn)}>{cn.cn_number || cn.creditnote_number}</td>
                <td className="p-3 cursor-pointer" onClick={() => setSelectedCN(cn)}>{custName}</td>
                <td className="p-3 text-[#64748B]">{cn.clinic_name || cn.location_name || '-'}</td>
                <td className="p-3 text-[#64748B]">{cn.date || cn.cn_date || '-'}</td>
                <td className="p-3 font-mono text-xs">{linkedInv || <span className="text-gray-400 font-sans">Standalone</span>}</td>
                <td className="p-3 text-xs text-[#64748B]" data-testid={`cn-lead-source-${cn.cn_number}`}>{cn.lead_source || '-'}</td>
                <td className="p-3 text-right font-medium text-red-600">{formatCurrency(cn.grand_total || cn.total)}</td>
                <td className="p-3 text-right text-[#64748B]">{formatCurrency(cn.balance || 0)}</td>
                <td className="p-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${cn.status === 'closed' ? 'bg-emerald-50 text-emerald-700' : cn.status === 'open' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{cn.status || '-'}</span>
                </td>
                <td className="p-3 text-xs text-[#64748B] max-w-[200px] truncate">{cn.reason || cn.notes || '-'}</td>
                <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" title="Edit" onClick={() => setEditCN(cn)} data-testid={`cn-edit-${cn.cn_number}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" title="Delete" onClick={() => handleDeleteCN(cn)} data-testid={`cn-delete-${cn.cn_number}`}><Trash size={14} /></Button>
                  </div>
                </td>
              </tr>
            );}) : (
              <tr><td colSpan={12} className="text-center py-8 text-[#64748B]">No credit notes found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#64748B]">Page {page} of {Math.ceil(total / pageSize)}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="cn-prev-page">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)} data-testid="cn-next-page">Next</Button>
          </div>
        </div>
      )}

      <CreditNoteDetailDialog cn={selectedCN} open={!!selectedCN} onClose={() => setSelectedCN(null)} />
      <EditCreditNoteDialog cn={editCN} onClose={() => setEditCN(null)} onSaved={() => { setEditCN(null); load(); }} />
    </div>
  );
}

/* ─── Edit Credit Note Dialog (admin) ─────────────────────── */
function EditCreditNoteDialog({ cn, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cn) { setForm(null); return; }
    setForm({
      customer_name: cn.customer_name || cn.customer?.name || '',
      cn_date: cn.cn_date || cn.date || '',
      reason: cn.reason || '',
      notes: cn.notes || '',
    });
    setItems((cn.line_items || []).map(li => ({
      item_id: li.item_id || '',
      name: li.name || li.item_name || '',
      hsn_sac: li.hsn_sac || li.hsn || '',
      quantity: li.quantity ?? 1,
      rate: li.rate ?? 0,
      discount_percent: li.discount_percent ?? 0,
      tax_percent: li.tax_percent ?? 0,
      serial_numbers: li.serial_numbers || [],
    })));
  }, [cn]);

  const upd = (i, f, v) => setItems(p => p.map((it, idx) => idx === i ? { ...it, [f]: v } : it));
  const addItem = () => setItems(p => [...p, { item_id: '', name: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, tax_percent: 0, serial_numbers: [] }]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0), 0);
  const totalTax = items.reduce((s, li) => {
    const line = (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0);
    const disc = line * ((parseFloat(li.discount_percent) || 0) / 100);
    return s + (line - disc) * ((parseFloat(li.tax_percent) || 0) / 100);
  }, 0);
  const grandTotal = subtotal + totalTax;

  const save = async () => {
    if (!items.length || !items.some(li => (li.name || '').trim())) { toast.error('At least one item is required'); return; }
    setSaving(true);
    try {
      await billingAPI.adminEditCreditNote(cn.id || cn.cn_number, {
        customer_name: form.customer_name,
        cn_date: form.cn_date,
        reason: form.reason,
        notes: form.notes,
        line_items: items.filter(li => (li.name || '').trim()).map(li => ({
          item_id: li.item_id || '',
          name: li.name,
          hsn_sac: li.hsn_sac || '',
          quantity: parseFloat(li.quantity) || 0,
          rate: parseFloat(li.rate) || 0,
          discount_percent: parseFloat(li.discount_percent) || 0,
          tax_percent: parseFloat(li.tax_percent) || 0,
          serial_numbers: li.serial_numbers || [],
        })),
      });
      toast.success(`Credit note ${cn.cn_number || ''} updated`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credit note');
    } finally { setSaving(false); }
  };

  if (!cn || !form) return null;
  return (
    <Dialog open={!!cn} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="cn-edit-dialog">
        <DialogHeader><DialogTitle>Edit Credit Note — {cn.cn_number || cn.creditnote_number}</DialogTitle>
          <DialogDescription>Update customer, date, reason and line items. Totals and GST are recalculated automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Customer</label>
              <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="cn-edit-customer" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Date</label>
              <Input type="date" value={form.cn_date} onChange={e => setForm(f => ({ ...f, cn_date: e.target.value }))} data-testid="cn-edit-date" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Reason</label>
            <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} data-testid="cn-edit-reason" />
          </div>

          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="cn-edit-items">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                  <th className="text-left p-2">Item</th>
                  <th className="text-center p-2 w-16">Qty</th>
                  <th className="text-right p-2 w-24">Rate</th>
                  <th className="text-center p-2 w-16">Disc%</th>
                  <th className="text-center p-2 w-16">Tax%</th>
                  <th className="text-right p-2 w-24">Total</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((li, i) => {
                  const line = (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0);
                  const disc = line * ((parseFloat(li.discount_percent) || 0) / 100);
                  const taxable = line - disc;
                  const tot = taxable + taxable * ((parseFloat(li.tax_percent) || 0) / 100);
                  return (
                    <tr key={i} className="border-t border-[#E2E8F0]">
                      <td className="p-2"><Input value={li.name} onChange={e => upd(i, 'name', e.target.value)} className="h-8 text-xs" data-testid={`cn-edit-item-name-${i}`} /></td>
                      <td className="p-2"><Input type="number" value={li.quantity} onChange={e => upd(i, 'quantity', e.target.value)} className="h-8 text-xs text-center" data-testid={`cn-edit-item-qty-${i}`} /></td>
                      <td className="p-2"><Input type="number" value={li.rate} onChange={e => upd(i, 'rate', e.target.value)} className="h-8 text-xs text-right" data-testid={`cn-edit-item-rate-${i}`} /></td>
                      <td className="p-2"><Input type="number" value={li.discount_percent} onChange={e => upd(i, 'discount_percent', e.target.value)} className="h-8 text-xs text-center" /></td>
                      <td className="p-2"><Input type="number" value={li.tax_percent} onChange={e => upd(i, 'tax_percent', e.target.value)} className="h-8 text-xs text-center" /></td>
                      <td className="p-2 text-right text-xs font-medium">{formatCurrency(tot)}</td>
                      <td className="p-2">{items.length > 1 && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeItem(i)}><Trash size={14} /></Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={addItem} data-testid="cn-edit-add-item">+ Add Item</Button>
            <div className="text-right text-sm">
              <div className="text-[#64748B]">Subtotal: {formatCurrency(subtotal)} · Tax: {formatCurrency(totalTax)}</div>
              <div className="font-bold text-red-600">Credit Total: {formatCurrency(grandTotal)}</div>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Notes</label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="cn-edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cn-edit-cancel">Cancel</Button>
          <Button className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={save} disabled={saving} data-testid="cn-edit-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

